/**
 * API Client for DMARC Platform
 * Handles all communication with the backend API
 */

const API_BASE = import.meta.env.VITE_API_URL || "";

// Demo mode for marketing screenshots - bypasses API and returns mock data
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";

// Import mock data for demo mode
import {
  mockDomains,
  mockAlerts,
  mockReports,
  mockSources,
  mockRemediationTasks,
  mockDashboardStats,
  mockRemediationStats,
  mockSourceStats,
  mockApexLinks,
  mockOrganization,
  mockAnalyticsData,
  mockTierPermissions,
} from "./mockData";

/**
 * Get the full URL for auth endpoints (Google/GitHub OAuth)
 * In production, auth routes are on the API domain
 */
export function getAuthUrl(path: string): string {
  return `${API_BASE}${path}`;
}

// Types
export interface User {
  id: string;
  organization_id: string;
  role: "owner" | "admin" | "member" | "readonly";
  email: string;
  name: string;
}

export interface Organization {
  id: string;
  name: string;
  billing_tier: "free" | "starter" | "pro" | "enterprise" | "enterprise_plus";
  tier?: string;
  industry_vertical?: string;
  company_size?: string;
  onboarding_completed?: boolean;
  // MSP fields
  organization_type?: "standard" | "msp" | "msp_client";
  parent_organization_id?: string | null;
  // MSP Partner tier - determines features and Verkh wholesale pricing
  partner_tier?: "reseller" | "white_label" | null;
  verkh_wholesale_cents_per_client?: number | null;
  // Stores the plan selected during signup (before payment) - used to redirect to checkout after onboarding
  signup_selected_plan?: "starter" | "pro" | "enterprise" | "enterprise_plus" | null;
  domain_count?: number;
  // Logo (Enterprise+ only)
  logo_url?: string;
  // Billing status fields
  billing_status?: "none" | "active" | "past_due" | "canceled" | "trialing" | "requires_action";
  billing_method?: "card" | "invoice";
  payment_terms?: "immediate" | "net15" | "net30";
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  cancel_effective_at?: string;
  downgrade_scheduled_tier?: string;
  downgrade_effective_at?: string;
  failed_payment_count?: number;
  last_payment_failure_at?: string;
  service_activated_at?: string;
  late_fee_start_date?: string;
  payment_method_brand?: string;
  payment_method_last4?: string;
  stripe_subscription_id?: string;
  // Billing address fields
  billing_name?: string;
  billing_email?: string;
  billing_address_line1?: string;
  billing_address_line2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_postal_code?: string;
  billing_country?: string;
  tax_id?: string;
  tax_id_type?: "vat" | "gst" | "ein" | "abn" | "other";
  created_at: string;
}

export interface Domain {
  id: string;
  domain_name: string;
  verification_status: "pending" | "verified";
  verification_token: string;
  policy_goal: "none" | "quarantine" | "reject";
  monitoring_status: "active" | "paused" | "archived";
  current_policy?: string;
  current_dmarc?: string;
  dmarc_policy?: string;
  spf_configured?: boolean;
  dkim_configured?: boolean;
  pass_rate?: number;
  recent_pass_rate?: number;
  total_messages?: number;
  created_at: string;
  // Verification expiry fields for unverified domains
  verification_expires_at?: string;
  days_until_expiry?: number;
}

export interface Alert {
  id: string;
  domain_id: string;
  domain_name: string;
  alert_type: string;
  severity: "critical" | "warning" | "info";
  title: string;
  description: string;
  message?: string;
  status: "open" | "acknowledged" | "resolved" | "snoozed";
  acknowledged?: boolean;
  resolved?: boolean;
  metadata?: Record<string, unknown>;
  created_at: string;
  resolved_at?: string;
}

// BIMI Types
export type BimiCheckStatus = "pass" | "fail" | "unknown";

export interface BimiReadinessCheck {
  id: string;
  label: string;
  description: string;
  status: BimiCheckStatus;
  passed: boolean;
}

export interface BimiReadinessResponse {
  domain_id: string;
  domain_name: string;
  is_ready: boolean;
  score: number;
  checks: BimiReadinessCheck[];
  checked_at: string;
}

export interface BimiLogoValidationCheck {
  name: string;
  passed: boolean;
  message: string;
}

export interface BimiLogoValidationResult {
  is_valid: boolean;
  logo_url: string;
  checks: BimiLogoValidationCheck[];
  preview_available: boolean;
}

export interface BimiRecordGeneratorResponse {
  host: string;
  value: string;
  record_type: "TXT";
  selector: string;
  domain_name: string;
}

export interface Source {
  id: string;
  domain_id?: string;
  domain_name?: string;
  identifier: string;
  identifier_type: "ip" | "hostname";
  source_ip?: string;
  ptr_hostname?: string;
  provider_name?: string;
  source_name?: string;
  org_name?: string;
  authorization_status: "authorized" | "unauthorized" | "pending_review" | "ignored";
  approved?: boolean;
  total_message_count: number;
  pass_count?: number;
  fail_count?: number;
  // Alignment pass rates (DMARC requirement - does domain align with header_from?)
  spf_pass_rate?: number;
  dkim_pass_rate?: number;
  // Auth pass rates (raw SPF/DKIM check passed, regardless of alignment)
  spf_auth_pass_rate?: number;
  dkim_auth_pass_rate?: number;
  // Mail From domain (envelope_from) - helps identify what the source claims to be
  common_envelope_from?: string;
  first_seen_at: string;
  last_seen_at: string;
  // Labeling fields
  label_category?: string;
  label_subtype?: string;
  labeled_at?: string;
  // Forwarder classification
  source_type?: "direct" | "forwarder" | "mailing_list" | "trusted_forwarder" | "arc_forwarder" | "unknown";
  forwarding_detected?: boolean;
  // AI Classification fields
  ai_classification?: string;
  ai_confidence?: "high" | "medium" | "low";
  ai_reasoning?: string;
  ai_analyzed_at?: string;
  auto_labeled?: boolean;
  user_confirmed?: boolean;
  // Provider matching (from known_providers FK)
  matched_provider_id?: string;
  known_provider_name?: string;
  known_provider_category?: string;
  // Tier restriction flag
  _tier_restricted?: boolean;
}

export interface SourcesResponse {
  sources: Source[];
  tier: string;
  permissions: {
    canViewHostname: boolean;
    canLabelSources: boolean;
    canViewRemediation: boolean;
    canApplyRemediation: boolean;
  };
}

// Enforcement Readiness Types
export interface BlockingSource {
  id: string;
  identifier: string;
  name: string | null;
  providerName: string | null;
  trustScore: number | null;
  authorizationStatus: string;
  spfPassRate: number;
  dkimPassRate: number;
  totalMessages: number;
  issue: string;
  fix: string;
}

export interface EnforcementBlocker {
  type: "spf" | "dkim" | "pass_rate" | "sources" | "forwarding";
  severity: "critical" | "high" | "medium" | "low";
  title: string;
  description: string;
  affectedMessages: number;
  remediation: {
    action: string;
    link?: string;
  };
}

export interface EnforcementRecommendation {
  type: "upgrade_policy" | "fix_source" | "review_source" | "configure_auth";
  title: string;
  description: string;
  link: string;
  priority: number;
  domainName?: string;
}

export interface DomainReadiness {
  id: string;
  domainName: string;
  currentPolicy: "none" | "quarantine" | "reject" | null;
  targetPolicy: "none" | "quarantine" | "reject";
  riskSimulation: {
    currentDisposition: { pass: number; quarantine: number; reject: number };
    atQuarantine: { wouldFail: number; percentage: number };
    atReject: { wouldFail: number; percentage: number };
    totalMessages: number;
    timeframeDays: number;
  };
  authentication: {
    spfConfigured: boolean;
    spfPassRate: number;
    dkimConfigured: boolean;
    dkimPassRate: number;
    dmarcPassRate: number;
  };
  sources: {
    total: number;
    authorized: number;
    pendingReview: number;
    unauthorized: number;
    blockingSources: BlockingSource[];
  };
  blockers: EnforcementBlocker[];
  readinessScore: number;
  canAdvance: boolean;
  nextStep: string;
  readinessStatement: string; // Plain English explanation of readiness status
  recommendedAction: EnforcementRecommendation | null;
}

export interface EnforcementReadinessResponse {
  domains: DomainReadiness[];
  summary: {
    totalDomains: number;
    atReject: number;
    atQuarantine: number;
    atNone: number;
    readyToAdvance: number;
    overallProgress: number;
  };
  recommendations: EnforcementRecommendation[];
  permissions: {
    canViewSources: boolean;
    canLabelSources: boolean;
    canViewRemediation: boolean;
  };
}

export interface LabelSourceResponse {
  success: boolean;
  label: { category: string; subtype: string };
  // Async task generation (new pattern)
  jobId?: string;
  tasksGenerating?: boolean;
  // Sync task generation (legacy, may be removed)
  tasksGenerated?: number;
  tasks?: Array<{
    id: string;
    title: string;
    effort: string;
    risk: string;
    canAutoApply: boolean;
    apexRecommended: boolean;
  }>;
  permissions: {
    canApplyRemediation: boolean;
    canGenerateApex: boolean;
  };
}

export interface BulkLabelResponse {
  success: boolean;
  labeledCount: number;
  skippedCount: number;
  category: string;
  subtype: string;
  authorizationStatus: string;
}

export interface AutoLabelResult {
  sourceId: string;
  identifier: string;
  sourceName: string | null;
  providerName: string | null;
  previousStatus: string | null;
  newStatus: string;
  newCategory: string;
  newSubtype: string;
  reason: string;
  confidence: "high" | "medium" | "low";
}

export interface AutoLabelResponse {
  success: boolean;
  summary: {
    totalAnalyzed: number;
    sourcesNeedingLabels: number;
    autoAuthorized: number;
    flaggedSuspicious: number;
    needsReview: number;
    highConfidence: number;
    mediumConfidence: number;
    lowConfidence: number;
    dryRun: boolean;
    updatedCount: number;
  };
  results: AutoLabelResult[];
  message: string;
}

export interface AutoLabelPreview {
  totalSources: number;
  unlabeledSources: number;
  withKnownProvider: number;
  detectedForwarders: number;
  estimatedAutoLabeled: number;
  hint: string;
}

export interface TaskGenerationStatusResponse {
  generating: boolean;
  statusMessage: string | null;
  pendingJobs: Array<{
    id: string;
    sourceId: string;
    sourceName: string;
    labelCategory: string;
    status: string;
    createdAt: string;
  }>;
  completedJobs: Array<{
    id: string;
    sourceId: string;
    sourceName: string;
    labelCategory: string;
    status: string;
    tasksCreated: number;
    tasksSkipped: number;
    aiReasoning: string;
    skippedReasons: Array<{ taskType: string; reason: string }>;
    completedAt: string;
  }>;
}

export interface SourceDailyStat {
  date: string;
  message_count: number;
  // Alignment pass/fail (DMARC alignment results)
  spf_pass_count: number;
  spf_fail_count: number;
  dkim_pass_count: number;
  dkim_fail_count: number;
  // Auth pass (raw SPF/DKIM authentication, optional - may not be present in older data)
  spf_auth_pass_count?: number;
  dkim_auth_pass_count?: number;
  dmarc_pass_count: number;
  dmarc_fail_count: number;
}

export interface DkimSelectorStat {
  selector: string;
  pass: number;
  fail: number;
  total: number;
  passRate: number;
}

export interface DailyDetailSelectorStat {
  selector: string;
  dkim_domain?: string | null;
  pass: number;
  fail: number;
  total: number;
}

export interface DailyDetail {
  date: string;
  total_messages: number;
  spf_pass: number;
  spf_fail: number;
  dmarc_pass?: number;
  dmarc_fail?: number;
  disposition_none?: number;
  disposition_quarantine?: number;
  disposition_reject?: number;
  envelope_from?: string[];
  header_from?: string[];
  selectors: DailyDetailSelectorStat[];
}

export interface SourceMetadata {
  dkim_selectors: string[];
  dkim_selector_stats?: DkimSelectorStat[];
  envelope_from_domains: string[];
  daily_detail?: DailyDetail[];
}

export interface SourceStatsBatchResponse {
  stats: Record<string, SourceDailyStat[]>;
  metadata?: Record<string, SourceMetadata>;
}

export interface RemediationSummary {
  totalIssues: number;
  criticalIssues: number;
  highIssues: number | null;
  mediumIssues: number | null;
  lowIssues: number | null;
  lastUpdated?: string;
  tier: string;
  upgradeMessage?: string;
}

export interface RemediationTask {
  id: string;
  domain_id: string;
  source_id?: string;
  task_type: string;
  title: string;
  description: string;
  priority: "critical" | "high" | "medium" | "low";
  status: "pending" | "in_progress" | "completed" | "skipped" | "blocked";
  category?: string;
  type?: string;
  suggested_record?: string;
  domain_name: string;
  due_date?: string;
  created_at: string;
  // Automation capabilities
  can_auto_apply?: boolean;
  requires_vendor_action?: boolean;
  apex_recommended?: boolean;
  // Provider info
  provider_id?: string;
  provider_guide_url?: string;
  // Insight pipeline link
  insight_id?: string;
  // Recurrence tracking
  recurrence_count?: number;
  previous_task_id?: string;
}

export interface DkimSelector {
  id: string;
  selector: string;
  discovered_from: "discovered" | "manual";
  is_valid: boolean;
  key_type?: string;
  provider_hint?: string;
  last_checked_at?: string;
  validation_errors?: string;
}

// DKIM Selector Usage with source mapping
export interface DkimSelectorSource {
  sourceId: string | null;
  sourceName: string | null;
  sourceIdentifier: string | null;
  authorizationStatus: string | null;
  messageCount: number;
  passRate: number;
}

export interface DkimSelectorUsage {
  id: string | null;
  selector: string;
  providerHint: string | null;
  isValid: boolean | null;
  validationErrors: string[] | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  lastCheckedAt: string | null;
  usage: {
    totalMessages: number;
    passCount: number;
    failCount: number;
    passRate: number;
    periodStart: string | null;
    periodEnd: string | null;
  };
  sources: DkimSelectorSource[];
  totalSources: number;
  _untracked?: boolean;
}

export interface DkimSelectorUsageResponse {
  domainName: string;
  selectors: DkimSelectorUsage[];
  summary: {
    totalSelectors: number;
    validSelectors: number;
    invalidSelectors: number;
    untrackedSelectors: number;
  };
  tier: string;
  tierRestrictions: {
    canViewSourceDetails: boolean;
    canLabelSources: boolean;
  };
}

export interface SigningDomainProvider {
  provider_id: string | null;
  provider_name: string;
}

export interface SigningDomain {
  dkim_domain: string;
  aligned: boolean;
  is_forwarder: boolean;
  total_messages: number;
  dkim_pass: number;
  dkim_fail: number;
  pass_rate: number;
  ip_count: number;
  selectors: string[];
  providers: SigningDomainProvider[];
}

export interface SigningDomainsResponse {
  signing_domains: SigningDomain[];
  domain_name: string;
  misaligned_count: number;
  total_signing_domains: number;
}

export interface Milestone {
  type: string;
  label: string;
  order: number;
  achieved: boolean;
  achieved_at?: string;
}

export interface ApexLink {
  id: string;
  domain_id: string;
  domain_name?: string;
  token: string;
  title?: string;
  source_ids?: string[];
  custom_message?: string;
  expires_at?: string;
  view_count: number;
  last_viewed_at?: string;
  status: "active" | "expired" | "revoked";
  created_at: string;
  created_by_user_id?: string;
  created_by_name?: string;
}

export interface ApexLinkAnalytics {
  total_views: number;
  unique_visitors: number;
  views_by_day: Array<{ date: string; count: number }>;
  top_referrers: Array<{ referrer: string; count: number }>;
  avg_time_on_page?: number;
}

export interface DnsIntegration {
  id: string;
  provider_type: "cloudflare" | "godaddy";
  provider_name: string;
  domain_id: string | null;
  zone_id: string | null;
  zone_name: string | null;
  status: "pending" | "connected" | "error" | "expired" | "revoked";
  last_error: string | null;
  last_sync_at: string | null;
  last_health_check_at: string | null;
  created_at: string;
}

export interface DnsZone {
  id: string;
  name: string;
  status?: string;
}

export interface DnsRecord {
  id: string;
  type: "TXT" | "CNAME" | "MX" | "A" | "AAAA";
  name: string;
  content: string;
  ttl: number;
}

export interface DnsDetectionResult {
  provider: "cloudflare" | "godaddy" | null;
  confidence: "low" | "medium" | "high";
  nameservers: string[];
  unsupportedProvider?: string;
  canAutoConnect: boolean;
  cached: boolean;
  detectedAt?: string;
}

export interface DnsOperation {
  id: string;
  operation_type: "create" | "update" | "delete";
  record_type: string;
  record_name: string;
  record_value: string | null;
  previous_value: string | null;
  triggered_by: "user" | "remediation" | "auto_verify";
  status: "pending" | "success" | "failed";
  error_message: string | null;
  provider_type: string;
  provider_name: string;
  user_name: string | null;
  user_email: string | null;
  created_at: string;
}

export interface IpWhitelistEntry {
  id: string;
  organization_id: string;
  ip_address: string;
  description: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  created_at: string;
  expires_at: string | null;
  is_active: number;
  last_used_at: string | null;
  use_count: number;
}

// SPF Optimization Types
export interface SpfResolveResult {
  record: string;
  lookup_count: number;
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  flattened_ipv4?: string[];
  flattened_ipv6?: string[];
  mechanism_tree?: unknown[];
}

export interface SpfChange {
  type: "added" | "removed" | "modified" | "consolidated" | "reordered" | "qualifier_change";
  mechanism: string;
  reason: string;
}

export interface SpfOptimizationResult {
  optimization_id: string;
  domain: string;
  current_spf: string | null;
  suggested_spf: string;
  diff: {
    before: string;
    after: string;
    changes: SpfChange[];
  };
  lookup_count: {
    before: number;
    after: number;
  };
  explanation: {
    summary: string;
    key_changes: string[];
    technical_notes?: string;
    risk_note?: string | null;
    recommendation?: string;
  };
  risk_level: "safe" | "caution" | "risky";
  validation: {
    is_valid: boolean;
    warnings: string[];
  };
  generated_at: string;
  can_apply: boolean;
}

// Error handling
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// API Client
class ApiClient {
  private token: string | null = null;
  private refreshPromise: Promise<string> | null = null;
  private readonly REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // Refresh if <5 mins remaining

  // MSP client context - when set, requests include X-Client-Organization-Id header
  private clientOrgId: string | null = null;

  // Request cache for deduplication and short-term caching
  private requestCache = new Map<string, { data: unknown; timestamp: number; promise?: Promise<unknown> }>();
  private readonly CACHE_TTL_MS = 30 * 1000; // 30 seconds cache TTL

  /**
   * Set the active client organization for MSP context.
   * When set, all API requests will include the X-Client-Organization-Id header,
   * causing the API to return that client's data instead of the MSP's own data.
   */
  setClientOrg(clientOrgId: string | null) {
    if (this.clientOrgId !== clientOrgId) {
      this.clientOrgId = clientOrgId;
      // Clear cache when switching context to ensure fresh data
      this.clearCache();
    }
  }

  /**
   * Get the current client organization ID (if viewing as a client)
   */
  getClientOrg(): string | null {
    return this.clientOrgId;
  }

  /**
   * Clear the entire request cache
   */
  clearCache() {
    this.requestCache.clear();
  }

  /**
   * Clear a specific cache entry by key pattern
   */
  clearCacheByPattern(pattern: string) {
    for (const key of this.requestCache.keys()) {
      if (key.includes(pattern)) {
        this.requestCache.delete(key);
      }
    }
  }

  /**
   * Generate a context-aware cache key that includes the client org ID.
   * This ensures cache entries are scoped to the current organization context.
   */
  private cacheKey(key: string): string {
    return this.clientOrgId ? `${key}:client=${this.clientOrgId}` : key;
  }

  /**
   * Get a cached response if fresh, or execute the request
   * Also deduplicates in-flight requests to the same endpoint
   */
  private async cachedRequest<T>(
    cacheKey: string,
    fetcher: () => Promise<T>,
    options?: { ttl?: number; skipCache?: boolean }
  ): Promise<T> {
    const ttl = options?.ttl ?? this.CACHE_TTL_MS;

    // Skip cache if requested
    if (options?.skipCache) {
      return fetcher();
    }

    const cached = this.requestCache.get(cacheKey);
    const now = Date.now();

    // Return fresh cached data
    if (cached && now - cached.timestamp < ttl) {
      // If there's an in-flight request, wait for it
      if (cached.promise) {
        return cached.promise as Promise<T>;
      }
      return cached.data as T;
    }

    // Deduplicate in-flight requests
    if (cached?.promise) {
      return cached.promise as Promise<T>;
    }

    // Execute the request
    const promise = fetcher();

    // Store the promise for deduplication
    this.requestCache.set(cacheKey, {
      data: cached?.data,
      timestamp: cached?.timestamp ?? 0,
      promise,
    });

    try {
      const data = await promise;
      // Cache the result
      this.requestCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });
      return data;
    } catch (error) {
      // Remove failed request from cache
      this.requestCache.delete(cacheKey);
      throw error;
    }
  }

  /**
   * @deprecated Token storage in localStorage is being phased out.
   * Authentication now uses HTTP-only cookies. This method is kept
   * for backward compatibility during the migration period.
   *
   * After the migration, this will only clear the in-memory token
   * and remove any legacy localStorage entries.
   */
  setToken(token: string | null) {
    this.token = token;
    // Clean up any legacy localStorage entries (migration period)
    // We no longer write new tokens to localStorage for security
    if (!token) {
      localStorage.removeItem("auth_token");
    }
  }

  /**
   * @deprecated Token retrieval from localStorage is being phased out.
   * Authentication now uses HTTP-only cookies sent automatically with requests.
   * This method is kept for backward compatibility during the migration period.
   */
  getToken(): string | null {
    // During migration, check localStorage for legacy tokens
    // After migration is complete, this will only return in-memory token
    if (!this.token) {
      const legacyToken = localStorage.getItem("auth_token");
      if (legacyToken) {
        // Migrate: use the token but don't re-save it
        this.token = legacyToken;
        // Clear from localStorage for security
        localStorage.removeItem("auth_token");
      }
    }
    return this.token;
  }

  /**
   * Exchange an authorization code for a session.
   * The session is established via HTTP-only cookie (set by the server).
   * Returns user info for the auth store to use.
   */
  async exchangeCode(code: string): Promise<{
    success: boolean;
    isNewUser: boolean;
    user: { id: string; organization_id: string; role: string };
  }> {
    const response = await fetch(`${API_BASE}/auth/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
      credentials: "include", // Important: receive and store HTTP-only cookie
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        response.status,
        data.code || "EXCHANGE_FAILED",
        data.error || "Failed to complete authentication"
      );
    }

    // Clear any in-memory token since we're using cookies now
    this.token = null;

    return data;
  }

  /**
   * Check if the user has an active session via HTTP-only cookie.
   * This replaces the old token-based auth check.
   */
  async hasValidSession(): Promise<boolean> {
    try {
      await this.getMe();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Decode JWT payload without validation (validation happens server-side)
   */
  private decodeToken(token: string): { exp?: number; sub?: string } | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      const payload = parts[1];
      if (!payload) return null;
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /**
   * Check if token is expired or about to expire
   */
  private isTokenExpiring(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload?.exp) return false;

    const expiresAt = payload.exp * 1000; // Convert to ms
    const now = Date.now();

    return expiresAt - now < this.REFRESH_THRESHOLD_MS;
  }

  /**
   * Refresh the token if it's about to expire
   * Uses a singleton promise to prevent concurrent refresh requests
   */
  private async ensureValidToken(): Promise<string | null> {
    const token = this.getToken();
    if (!token) return null;

    // Check if token needs refresh
    if (!this.isTokenExpiring(token)) {
      return token;
    }

    // If already refreshing, wait for that to complete
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start refresh
    this.refreshPromise = this.performTokenRefresh(token);

    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(currentToken: string): Promise<string> {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
        credentials: "include", // Include HTTP-only cookies
      });

      if (!response.ok) {
        // If refresh fails with 401, token is invalid - clear it
        if (response.status === 401) {
          this.setToken(null);
          throw new ApiError(401, "SESSION_EXPIRED", "Your session has expired. Please log in again.");
        }
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      if (data.token) {
        this.setToken(data.token);
        return data.token;
      }

      throw new Error("No token in refresh response");
    } catch (error) {
      // If refresh fails, fall back to current token
      // It might still work for this request
      console.error("Token refresh failed:", error);
      return currentToken;
    }
  }

  /**
   * Handle demo mode by returning mock data instead of making API calls
   */
  private getMockData<T>(method: string, path: string, params?: Record<string, string | number>): T | null {
    // Auth endpoints
    if (path === "/auth/me") {
      return {
        user: {
          id: "user_demo",
          organization_id: "org_demo",
          role: "owner",
          email: "demo@acmecorp.com",
          name: "Demo User",
        },
      } as T;
    }

    // Organization
    if (path === "/api/organization") {
      return {
        organization: {
          id: "org_demo",
          name: mockOrganization.name,
          billing_tier: mockOrganization.billing_tier,
          tier: mockOrganization.tier,
          domain_count: mockOrganization.domains_used,
          created_at: new Date().toISOString(),
        },
      } as T;
    }

    // Domains
    if (path === "/api/domains" && method === "GET") {
      return {
        domains: mockDomains.map((d) => ({
          ...d,
          verification_token: "demo_token_" + d.id,
        })),
      } as T;
    }

    // Single domain
    const domainMatch = path.match(/^\/api\/domains\/([^/]+)$/);
    if (domainMatch && method === "GET") {
      const domain = mockDomains.find((d) => d.id === domainMatch[1]);
      if (domain) {
        return { domain: { ...domain, verification_token: "demo_token" } } as T;
      }
    }

    // Domain stats
    const domainStatsMatch = path.match(/^\/api\/domains\/([^/]+)\/stats$/);
    if (domainStatsMatch) {
      const domain = mockDomains.find((d) => d.id === domainStatsMatch[1]);
      return {
        stats: {
          total_reports: mockReports.filter((r) => r.domainId === domainStatsMatch[1]).length,
          total_messages: domain?.total_messages || 0,
          total_records: mockReports.filter((r) => r.domainId === domainStatsMatch[1]).reduce((sum, r) => sum + r.recordCount, 0),
        },
        breakdown: [
          { dmarc_result: "pass", message_count: Math.round((domain?.total_messages || 0) * 0.94) },
          { dmarc_result: "fail", message_count: Math.round((domain?.total_messages || 0) * 0.06) },
        ],
      } as T;
    }

    // Domain sources
    const domainSourcesMatch = path.match(/^\/api\/domains\/([^/]+)\/sources$/);
    if (domainSourcesMatch) {
      return {
        sources: mockSources.filter((s) => s.domain_id === domainSourcesMatch[1]),
      } as T;
    }

    // All sources
    if (path === "/api/sources" && method === "GET") {
      const permissions = mockTierPermissions[mockOrganization.tier];
      return {
        sources: mockSources,
        tier: mockOrganization.tier,
        permissions: {
          canViewHostname: permissions.canViewHostname,
          canLabelSources: permissions.canLabelSources,
          canViewRemediation: permissions.canViewRemediation,
          canApplyRemediation: permissions.canApplyRemediation,
        },
      } as T;
    }

    // Alerts
    if (path === "/api/alerts" && method === "GET") {
      const status = params?.status as string || "open";
      const filtered = status === "all"
        ? mockAlerts
        : mockAlerts.filter((a) => a.status === status || (status === "open" && a.status === "acknowledged"));
      return { alerts: filtered } as T;
    }

    // Reports
    if (path === "/api/reports" && method === "GET") {
      return { reports: mockReports } as T;
    }

    // Reports analytics
    if (path === "/api/reports/analytics" && method === "GET") {
      const days = Number(params?.days) || 30;
      return {
        timeSeries: mockAnalyticsData.dailyTrend.slice(-days).map((d) => ({
          date: d.date,
          totalMessages: d.totalMessages,
          dmarcPassRate: d.passRate,
          spfPassRate: d.spfPassRate,
          dkimPassRate: d.dkimPassRate,
          dispositionNone: Math.round(d.totalMessages * 0.94),
          dispositionQuarantine: Math.round(d.totalMessages * 0.05),
          dispositionReject: Math.round(d.totalMessages * 0.01),
        })),
        topReporters: [
          { reporter: "google.com", reportCount: 45, totalMessages: 45230 },
          { reporter: "microsoft.com", reportCount: 32, totalMessages: 28450 },
          { reporter: "yahoo.com", reportCount: 18, totalMessages: 12340 },
        ],
        summary: {
          totalMessages: mockDashboardStats.totalEmailsProcessed,
          dmarcPassRate: mockDashboardStats.passRate,
          spfPassRate: 95.2,
          dkimPassRate: 96.8,
          domainsWithData: mockDomains.filter((d) => d.total_messages > 0).length,
        },
        granularity: "daily" as const,
        dateRange: {
          start: mockAnalyticsData.dailyTrend[0]?.date || new Date().toISOString(),
          end: mockAnalyticsData.dailyTrend[mockAnalyticsData.dailyTrend.length - 1]?.date || new Date().toISOString(),
          days,
        },
      } as T;
    }

    // Reports by source
    if (path === "/api/reports/by-source" && method === "GET") {
      return {
        sources: mockSources.map((s) => ({
          sourceName: s.source_name,
          sourceIdentifier: s.identifier,
          authorizationStatus: s.authorization_status,
          reportCount: Math.floor(Math.random() * 20) + 5,
          totalMessages: s.total_message_count,
          spfPass: s.pass_count,
          spfFail: s.fail_count,
          dkimPass: Math.round(s.pass_count * 0.98),
          dkimFail: Math.round(s.fail_count * 1.02),
          dmarcPass: s.pass_count,
          dmarcFail: s.fail_count,
          dispositionNone: s.pass_count,
          dispositionQuarantine: Math.round(s.fail_count * 0.7),
          dispositionReject: Math.round(s.fail_count * 0.3),
          firstSeen: s.first_seen_at,
          lastSeen: s.last_seen_at,
        })),
      } as T;
    }

    // Remediation tasks
    if (path === "/api/remediation-tasks" && method === "GET") {
      const status = params?.status as string;
      const filtered = status ? mockRemediationTasks.filter((t) => t.status === status) : mockRemediationTasks;
      return {
        tasks: filtered.map((t) => ({
          ...t,
          can_auto_apply: t.can_auto_apply,
          requires_vendor_action: t.requires_vendor_action,
          apex_recommended: t.apex_recommended,
        })),
      } as T;
    }

    // Remediation summary
    if (path === "/api/sources/remediation-summary" && method === "GET") {
      return {
        totalIssues: mockRemediationStats.total,
        criticalIssues: mockRemediationStats.criticalCount,
        highIssues: mockRemediationStats.highCount,
        mediumIssues: 2,
        lowIssues: 1,
        tier: mockOrganization.tier,
      } as T;
    }

    // APEX links
    if (path === "/api/apex-links" && method === "GET") {
      return {
        apex_links: mockApexLinks.map((link) => ({
          id: link.id,
          domain_id: link.domain_id,
          domain_name: link.domain_name,
          token: link.token,
          title: link.title,
          expires_at: link.expires_at,
          view_count: link.view_count,
          status: link.status,
          created_at: link.created_at,
        })),
        limits: { used: mockApexLinks.filter((l) => l.status === "active").length, max: 3 },
      } as T;
    }

    // Invitations
    if (path === "/api/invitations" && method === "GET") {
      return {
        invitations: [
          {
            id: "inv_1",
            email: "john.doe@acmecorp.com",
            role: "member",
            status: "pending",
            expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            created_at: new Date().toISOString(),
          },
        ],
      } as T;
    }

    // User profile
    if (path === "/api/user/profile" && method === "GET") {
      return {
        profile: {
          id: "user_demo",
          name: "Demo User",
          email: "demo@acmecorp.com",
          role: "owner",
          created_at: new Date().toISOString(),
        },
      } as T;
    }

    // User preferences
    if (path === "/api/user/preferences" && method === "GET") {
      return {
        preferences: {
          weekly_digest: true,
          alert_emails: true,
          alert_severities: ["critical", "warning"],
        },
      } as T;
    }

    // Events/Activity
    if (path === "/api/events" && method === "GET") {
      return {
        events: [
          { id: "evt_1", event_type: "domain.verified", entity_type: "domain", description: "Domain acmecorp.com was verified", created_at: new Date().toISOString() },
          { id: "evt_2", event_type: "alert.created", entity_type: "alert", description: "New critical alert: Unauthorized sender detected", created_at: new Date(Date.now() - 3600000).toISOString() },
          { id: "evt_3", event_type: "task.completed", entity_type: "task", description: "SPF record updated for marketing.acmecorp.com", created_at: new Date(Date.now() - 7200000).toISOString() },
        ],
        pagination: { total: 3, limit: 20, offset: 0, hasMore: false },
      } as T;
    }

    // Billing
    if (path === "/api/billing" && method === "GET") {
      return {
        billing: {
          current_tier: mockOrganization.tier,
          subscription: {
            id: "sub_demo",
            status: "active",
            current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            cancel_at_period_end: false,
          },
          payment_method: { brand: "visa", last4: "4242" },
          overage: null,
          invoices: [],
        },
      } as T;
    }

    // Source stats (for dashboard)
    if (path.includes("/source-stats")) {
      return mockSourceStats as T;
    }

    // Label options
    if (path === "/api/sources/labels/options" && method === "GET") {
      return {
        categories: ["authorized", "customer_support", "cicd", "marketing", "investigating", "malicious"],
        subtypes: {
          authorized: ["corporate", "transactional", "marketing"],
          customer_support: ["helpdesk", "ticketing", "live_chat"],
          cicd: ["infrastructure", "monitoring", "deployment"],
          marketing: ["newsletter", "campaigns", "drip"],
          investigating: ["unknown", "suspicious"],
          malicious: ["phishing", "spoofing", "spam"],
        },
      } as T;
    }

    // DNS integrations
    if (path === "/api/dns-integrations" && method === "GET") {
      return { integrations: [] } as T;
    }

    // API keys
    if (path === "/api/keys" && method === "GET") {
      return { keys: [] } as T;
    }

    // Default: return null to indicate no mock data available
    return null;
  }

  private async request<T>(
    method: string,
    path: string,
    options?: {
      body?: unknown;
      params?: Record<string, string | number>;
      skipTokenRefresh?: boolean;
    }
  ): Promise<T> {
    // In demo mode, return mock data instead of making API calls
    if (DEMO_MODE) {
      const mockData = this.getMockData<T>(method, path, options?.params);
      if (mockData !== null) {
        // Simulate network delay for realism
        await new Promise((resolve) => setTimeout(resolve, 100 + Math.random() * 200));
        return mockData;
      }
      // For unhandled endpoints, log a warning and return empty object
      console.warn(`[DEMO MODE] No mock data for ${method} ${path}`);
      return {} as T;
    }

    const url = new URL(`${API_BASE}${path}`, window.location.origin);

    if (options?.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    // Skip token refresh for auth endpoints to avoid infinite loops
    const token = options?.skipTokenRefresh
      ? this.getToken()
      : await this.ensureValidToken();

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // MSP client context - include header when viewing as a client
    if (this.clientOrgId) {
      headers["X-Client-Organization-Id"] = this.clientOrgId;
      console.log("[API] Sending request with client context:", this.clientOrgId, "to", url.pathname);
    }

    const response = await fetch(url.toString(), {
      method,
      headers,
      body: options?.body ? JSON.stringify(options.body) : undefined,
      credentials: "include", // Include HTTP-only cookies for authentication
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        response.status,
        data.code || "UNKNOWN_ERROR",
        data.error || "An error occurred",
        data.details
      );
    }

    return data;
  }

  // Auth
  async getMe(): Promise<{ user: User }> {
    return this.request("GET", "/auth/me");
  }

  // Organization
  async getOrganization(options?: { skipCache?: boolean }): Promise<{ organization: Organization }> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/organization"),
      () => this.request("GET", "/api/organization"),
      options
    );
  }

  // Dashboard compound endpoint - fetches all dashboard data in one call
  // Note: cache key includes client org ID to ensure fresh data when switching contexts
  async getDashboardSummary(): Promise<{
    organization: Organization;
    domains: Domain[];
    senders: {
      total: number;
      approved: number;
      pending: number;
      unauthorized: number;
    };
    sources: {
      total: number;
      approved: number;
      pending: number;
      unauthorized: number;
      labeled: number;
    };
    alerts: Alert[];
    remediationTasks: {
      total: number;
      pending: number;
      inProgress: number;
      completed: number;
      blocked: number;
      criticalCount: number;
      highCount: number;
    };
    emailStats: {
      totalMessages: number;
      dmarcPassCount: number;
      dmarcFailCount: number;
      spfPassCount: number;
      spfFailCount: number;
      dkimPassCount: number;
      dkimFailCount: number;
      passRate: number;
    };
    trends?: {
      passRateTrend: number;
      messagesTrend: number;
    };
    enforcement: {
      totalDomains: number;
      domainsAtEnforcement: number;
      enforcementPercentage: number;
    };
    permissions: {
      canViewRemediation: boolean;
      canViewSources: boolean;
      canLabelSources: boolean;
      tierName: string;
    };
  }> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/dashboard/summary"),
      () => this.request("GET", "/api/dashboard/summary"),
      { ttl: 60000 } // 1 minute cache for dashboard
    );
  }

  // Enforcement readiness - comprehensive enforcement data with risk simulation
  async getEnforcementReadiness(): Promise<EnforcementReadinessResponse> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/enforcement/readiness"),
      () => this.request("GET", "/api/enforcement/readiness"),
      { ttl: 60000 } // 1 minute cache
    );
  }

  // Policy advancement - generate DNS record or verify DNS update
  async advanceDomainPolicy(
    domainId: string,
    targetPolicy: "quarantine" | "reject",
    verify?: boolean
  ): Promise<{
    record?: string;
    hostName?: string;
    verified?: boolean;
    currentPolicy?: string | null;
    message?: string;
  }> {
    return this.request("POST", `/api/domains/${domainId}/advance-policy`, {
      body: { targetPolicy, verify },
    });
  }

  async updateOrganization(data: Partial<Organization>): Promise<{ success: boolean }> {
    return this.request("PATCH", "/api/organization", { body: data });
  }

  async uploadOrganizationLogo(file: File): Promise<{ logo_url: string }> {
    const formData = new FormData();
    formData.append("logo", file);

    const response = await fetch(`${API_BASE}/api/organization/logo`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Upload failed" }));
      throw new Error(error.error || "Upload failed");
    }

    return response.json();
  }

  async deleteOrganizationLogo(): Promise<{ success: boolean }> {
    return this.request("DELETE", "/api/organization/logo");
  }

  // Domains
  async getDomains(options?: { skipCache?: boolean }): Promise<{ domains: Domain[] }> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/domains"),
      () => this.request("GET", "/api/domains"),
      options
    );
  }

  async createDomain(domain_name: string): Promise<{ domain: Domain }> {
    const result = await this.request<{ domain: Domain }>("POST", "/api/domains", { body: { domain_name } });
    this.clearCacheByPattern("/api/domains");
    return result;
  }

  async getDomain(id: string): Promise<{ domain: Domain }> {
    return this.request("GET", `/api/domains/${id}`);
  }

  async getDomainDetails(id: string): Promise<{
    domain: Domain;
    sources: Source[];
    dkimSelectors: DkimSelector[];
    tier: string;
    permissions: { canViewHostname: boolean; canLabelSources: boolean };
  }> {
    return this.request("GET", `/api/domains/${id}/details`);
  }

  async verifyDomain(id: string): Promise<{ verified: boolean; message?: string }> {
    return this.request("POST", `/api/domains/${id}/verify`);
  }

  async getDomainStats(
    id: string,
    days: number = 30
  ): Promise<{
    stats: { total_reports: number; total_messages: number; total_records: number };
    breakdown: Array<{ dmarc_result: string; message_count: number }>;
  }> {
    return this.request("GET", `/api/domains/${id}/stats`, { params: { days } });
  }

  async getDomainSources(id: string): Promise<{ sources: Source[] }> {
    return this.request("GET", `/api/domains/${id}/sources`);
  }

  // Get all sources across all domains (single query, avoids N+1)
  // Returns tier-gated data based on organization's subscription
  async getAllSources(options?: { skipCache?: boolean }): Promise<SourcesResponse> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/sources"),
      () => this.request("GET", "/api/sources"),
      options
    );
  }

  // Get available label categories and subtypes
  async getLabelOptions(): Promise<{
    categories: readonly string[];
    subtypes: Record<string, readonly string[]>;
  }> {
    return this.request("GET", "/api/sources/labels/options");
  }

  // Label a source and generate remediation tasks
  async labelSource(
    sourceId: string,
    category: string,
    subtype: string,
    notes?: string
  ): Promise<LabelSourceResponse> {
    return this.request("POST", `/api/sources/${sourceId}/label`, {
      body: { category, subtype, notes },
    });
  }

  // Bulk label multiple sources (batches into chunks of 100 to match API limit)
  async bulkLabelSources(
    sourceIds: string[],
    category: string,
    subtype: string,
    notes?: string
  ): Promise<BulkLabelResponse> {
    const BATCH_SIZE = 100;

    if (sourceIds.length <= BATCH_SIZE) {
      return this.request("POST", "/api/sources/bulk-label", {
        body: { sourceIds, category, subtype, notes },
      });
    }

    // Split into chunks and run sequentially to avoid rate limiting
    let totalLabeled = 0;
    let totalSkipped = 0;
    let lastResult: BulkLabelResponse | null = null;

    for (let i = 0; i < sourceIds.length; i += BATCH_SIZE) {
      const chunk = sourceIds.slice(i, i + BATCH_SIZE);
      const result = await this.request<BulkLabelResponse>("POST", "/api/sources/bulk-label", {
        body: { sourceIds: chunk, category, subtype, notes },
      });
      totalLabeled += result.labeledCount;
      totalSkipped += result.skippedCount;
      lastResult = result;
    }

    return {
      success: true,
      labeledCount: totalLabeled,
      skippedCount: totalSkipped,
      category: lastResult!.category,
      subtype: lastResult!.subtype,
      authorizationStatus: lastResult!.authorizationStatus,
    };
  }

  // Authorize/label all sources for a known provider on a specific domain
  async authorizeProvider(
    providerId: string,
    domainId: string,
    category: string,
    subtype: string,
    notes?: string
  ): Promise<{ success: boolean; updatedCount: number; providerName: string; authorizationStatus: string }> {
    return this.request("POST", `/api/sources/provider/${encodeURIComponent(providerId)}/authorize`, {
      body: { category, subtype, domainId, notes },
    });
  }

  // Auto-label sources using AI-powered analysis
  async autoLabelSources(options?: {
    domainId?: string;
    dryRun?: boolean;
    onlyUnlabeled?: boolean;
  }): Promise<AutoLabelResponse> {
    return this.request("POST", "/api/sources/auto-label", {
      body: options || {},
    });
  }

  // Get auto-label preview (counts without making changes)
  async getAutoLabelPreview(domainId?: string): Promise<AutoLabelPreview> {
    const query = domainId ? `?domainId=${encodeURIComponent(domainId)}` : "";
    return this.request("GET", `/api/sources/auto-label/preview${query}`);
  }

  // Get remediation issue summary (free tier sees counts only)
  async getRemediationSummary(): Promise<RemediationSummary> {
    return this.request("GET", "/api/sources/remediation-summary");
  }

  // Get task generation status (for async task generation)
  async getTaskGenerationStatus(): Promise<TaskGenerationStatusResponse> {
    return this.request("GET", "/api/sources/task-generation-status");
  }

  // Get batch daily stats for multiple sources (for expandable source details)
  async getSourceStatsBatch(sourceIds: string[], days: number = 30): Promise<SourceStatsBatchResponse> {
    const ids = sourceIds.join(",");
    return this.request("GET", `/api/sources/stats/batch?ids=${encodeURIComponent(ids)}&days=${days}`);
  }

  // Backfill source daily stats from historical dmarc_report_records (Admin only)
  async backfillSourceStats(options?: {
    domainId?: string;
    sourceIds?: string[];
  }): Promise<{
    success: boolean;
    message: string;
    sourcesProcessed: number;
    datesProcessed: number;
  }> {
    return this.request("POST", "/api/admin/backfill-source-stats", {
      body: options || {},
    });
  }

  // Generate APEX link from remediation task
  async generateApexFromTask(taskId: string): Promise<{
    success: boolean;
    apexLink: {
      id: string;
      url: string;
      token: string;
      expiresAt: string | null;
      sourceName: string;
      taskId: string;
    };
    usage: {
      current: number;
      limit: number;
    };
  }> {
    return this.request("POST", `/api/remediation-tasks/${taskId}/generate-apex`);
  }

  // Revoke APEX link from remediation task
  async revokeApexFromTask(taskId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/remediation-tasks/${taskId}/revoke-apex`);
  }

  async getDomainMilestones(
    id: string
  ): Promise<{ milestones: Milestone[]; progress: Milestone[]; completion_percentage: number }> {
    return this.request("GET", `/api/domains/${id}/milestones`);
  }

  async getSuggestedSpf(id: string): Promise<{
    current_spf: string | null;
    suggested_spf: string;
    includes: string[];
    ip_addresses: string[];
    dns_lookup_estimate: number;
    warnings: string[];
  }> {
    return this.request("GET", `/api/domains/${id}/suggested-spf`);
  }

  async getSuggestedDmarc(id: string): Promise<{
    current_dmarc: string | null;
    current_policy: string | null;
    current_pct: number;
    pass_rate: number;
    policy_goal: string;
    recommendation: {
      action: string;
      suggested_record: string;
      reasoning: string;
      risk_level: "low" | "medium" | "high";
    };
  }> {
    return this.request("GET", `/api/domains/${id}/suggested-dmarc`);
  }

  async refreshDns(id: string): Promise<{
    dmarc: { raw: string | null; valid: boolean; errors: string[] };
    spf: { raw: string | null; valid: boolean; errors: string[] };
    mx: { raw: string | null; valid: boolean; errors: string[] };
    refreshed_at: string;
    next_refresh_available_at: string;
  }> {
    return this.request("POST", `/api/domains/${id}/refresh-dns`);
  }

  // DKIM
  async getDkimSelectors(domainId: string): Promise<{ selectors: DkimSelector[] }> {
    return this.request("GET", `/api/domains/${domainId}/dkim`);
  }

  async addDkimSelector(
    domainId: string,
    selector: string,
    provider_hint?: string
  ): Promise<{ selector: DkimSelector }> {
    return this.request("POST", `/api/domains/${domainId}/dkim`, {
      body: { selector, provider_hint },
    });
  }

  async validateDkimSelector(
    domainId: string,
    selectorId: string
  ): Promise<{ valid: boolean; error?: string; record?: string }> {
    return this.request("POST", `/api/domains/${domainId}/dkim/${selectorId}/validate`);
  }

  async deleteDkimSelector(domainId: string, selectorId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/domains/${domainId}/dkim/${selectorId}`);
  }

  // Get DKIM selector usage with source mapping
  async getDkimSelectorUsage(domainId: string): Promise<DkimSelectorUsageResponse> {
    return this.request("GET", `/api/domains/${domainId}/dkim/usage`);
  }

  // Signing Domains
  async getSigningDomains(domainId: string): Promise<SigningDomainsResponse> {
    return this.request("GET", `/api/sources/signing-domains?domainId=${domainId}`);
  }

  // BIMI (Brand Indicators for Message Identification)
  async getBimiReadiness(domainId: string): Promise<BimiReadinessResponse> {
    return this.request("GET", `/api/domains/${domainId}/bimi/readiness`);
  }

  async validateBimiLogo(
    domainId: string,
    logoUrl: string
  ): Promise<BimiLogoValidationResult> {
    return this.request("POST", `/api/domains/${domainId}/bimi/validate-logo`, {
      body: { logo_url: logoUrl },
    });
  }

  async generateBimiRecord(
    domainId: string,
    input: { logo_url: string; vmc_url?: string; selector?: string }
  ): Promise<BimiRecordGeneratorResponse> {
    return this.request("POST", `/api/domains/${domainId}/bimi/generate-record`, {
      body: input,
    });
  }

  // Sources
  async updateSource(
    id: string,
    data: { authorization_status?: string; provider_name?: string; notes?: string }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/sources/${id}`, { body: data });
  }

  async approveSource(id: string): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/sources/${id}`, { body: { authorization_status: "authorized" } });
  }

  // Recalculate pass rates from historical data
  async recalculatePassRates(domainId?: string): Promise<{
    success: boolean;
    message: string;
    rowsUpdated: number;
    domainId?: string;
  }> {
    if (domainId) {
      return this.request("POST", `/api/sources/recalculate-pass-rates/${domainId}`);
    }
    return this.request("POST", "/api/sources/recalculate-pass-rates");
  }

  // Backfill missing sending_sources from report records
  async backfillSources(): Promise<{
    success: boolean;
    message: string;
    found: number;
    created: number;
    errors: number;
  }> {
    return this.request("POST", "/api/sources/backfill");
  }

  // Alerts
  async getAlerts(status?: string, options?: { skipCache?: boolean }): Promise<{ alerts: Alert[] }> {
    const baseKey = `GET:/api/alerts${status ? `?status=${status}` : ""}`;
    return this.cachedRequest(
      this.cacheKey(baseKey),
      () => this.request("GET", "/api/alerts", status ? { params: { status } } : undefined),
      options
    );
  }

  async updateAlert(id: string, status: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/alerts/${id}`, { body: { status } });
    this.clearCacheByPattern("/api/alerts");
    return result;
  }

  async acknowledgeAlert(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/alerts/${id}`, { body: { status: "acknowledged" } });
    this.clearCacheByPattern("/api/alerts");
    return result;
  }

  async resolveAlert(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/alerts/${id}`, { body: { status: "resolved" } });
    this.clearCacheByPattern("/api/alerts");
    return result;
  }

  async snoozeInsight(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/ai/insights/${id}`, { body: { status: "snoozed" } });
    this.clearCacheByPattern("/api/ai/recommendations");
    return result;
  }

  async resolveInsight(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/ai/insights/${id}`, { body: { status: "resolved" } });
    this.clearCacheByPattern("/api/ai/recommendations");
    return result;
  }

  async suppressInsight(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/ai/insights/${id}`, { body: { status: "not_applicable" } });
    this.clearCacheByPattern("/api/ai/recommendations");
    this.clearCacheByPattern("/api/ai/suppressions");
    return result;
  }

  async getInsightSuppressions(): Promise<{ suppressions: AIInsightSuppression[] }> {
    return this.request<{ suppressions: AIInsightSuppression[] }>("GET", `/api/ai/suppressions`);
  }

  async deleteInsightSuppression(id: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("DELETE", `/api/ai/suppressions/${id}`);
    this.clearCacheByPattern("/api/ai/suppressions");
    return result;
  }

  // Reports
  async getReports(params?: { limit?: number; offset?: number; domainId?: string; days?: number }): Promise<{
    reports: Array<{
      id: string;
      reportId: string;
      orgName: string;
      dateRangeBegin: string;
      dateRangeEnd: string;
      domainId: string;
      domainName?: string;
      recordCount: number;
      totalMessageCount: number;
      processedAt: string;
      spfPass: number;
      spfFail: number;
      dkimPass: number;
      dkimFail: number;
      dmarcPass: number;
      dmarcFail: number;
      dispositionNone: number;
      dispositionQuarantine: number;
      dispositionReject: number;
    }>;
    summary: {
      totalMessages: number;
      dmarcCapableMessages: number;
      spfPass: number;
      spfFail: number;
      dkimPass: number;
      dkimFail: number;
      dmarcPass: number;
      dmarcFail: number;
      forwarderMessages: number;
    };
  }> {
    return this.request("GET", "/api/reports", { params });
  }

  // Get raw records for a specific report (drill-down)
  async getReportRecords(reportId: string): Promise<{
    report: {
      id: string;
      reportId: string;
      domainName: string;
    };
    records: Array<{
      source_ip: string;
      count: number;
      disposition: string;
      spf_result: string;
      spf_domain: string | null;
      dkim_result: string;
      dkim_domain: string | null;
      dmarc_result: string;
      header_from: string | null;
      envelope_from: string | null;
      source_ip_org: string | null;
      source_ip_asn: string | null;
    }>;
    summary: {
      totalRecords: number;
      uniqueSpfResults: string[];
      uniqueDkimResults: string[];
      uniqueDmarcResults: string[];
    };
  }> {
    return this.request("GET", `/api/reports/${reportId}/records`);
  }

  // Get reports aggregated by sending source (origin)
  // Now supports pagination - defaults to 1000 (API max) to get more sources
  async getReportsBySource(params?: { limit?: number; offset?: number; days?: number }): Promise<{
    sources: Array<{
      sourceName: string;
      sourceIdentifier: string;
      authorizationStatus: "authorized" | "unauthorized" | "pending_review" | "ignored" | null;
      ptrHostname: string | null;
      providerName: string | null;
      forwardingDetected: boolean;
      sourceType: string | null;
      dkimAuthPassRate: number | null;
      spfAuthPassRate: number | null;
      // DKIM selectors used by this source
      dkimSelectors: string[];
      // SPF Mail From (envelope_from) domain
      envelopeFrom: string | null;
      envelopeFromDomains: string[];
      reportCount: number;
      totalMessages: number;
      spfPass: number;
      spfFail: number;
      dkimPass: number;
      dkimFail: number;
      dmarcPass: number;
      dmarcFail: number;
      dispositionNone: number;
      dispositionQuarantine: number;
      dispositionReject: number;
      firstSeen: string;
      lastSeen: string;
    }>;
    summary: {
      totalSources: number;
      totalMessages: number;
      dmarcCapableMessages: number;
      spfPass: number;
      spfFail: number;
      dkimPass: number;
      dkimFail: number;
      dmarcPass: number;
      dmarcFail: number;
      forwarderMessages: number;
      forwarderSources: number;
    };
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    // Default to max limit (1000) to fetch more sources
    return this.request("GET", "/api/reports/by-source", {
      params: { limit: params?.limit ?? 1000, offset: params?.offset ?? 0, ...(params?.days ? { days: params.days } : {}) }
    });
  }

  // Get daily detail (per-date DKIM selector + SPF + disposition breakdown) for source IPs
  async getDailyDetail(ips: string[], days?: number): Promise<{ daily_detail: Record<string, DailyDetail[]> }> {
    return this.request("GET", "/api/reports/daily-detail", {
      params: { ips: ips.join(","), days: days ?? 30 },
    });
  }

  // Get analytics data for charts
  async getReportsAnalytics(params?: { days?: number; domainId?: string; startDate?: string; endDate?: string }): Promise<{
    timeSeries: Array<{
      date: string;
      totalMessages: number;
      dmarcPassRate: number | null;
      spfPassRate: number | null;
      dkimPassRate: number | null;
      dispositionNone: number;
      dispositionQuarantine: number;
      dispositionReject: number;
    }>;
    topReporters: Array<{
      reporter: string;
      reportCount: number;
      totalMessages: number;
    }>;
    summary: {
      totalMessages: number;
      dmarcPassRate: number | null;
      spfPassRate: number | null;
      dkimPassRate: number | null;
      domainsWithData: number;
    };
    granularity: "daily" | "weekly";
    dateRange: {
      start: string;
      end: string;
      days: number;
    };
  }> {
    return this.request("GET", "/api/reports/analytics", { params });
  }

  // Remediation
  async getRemediationTasks(
    domainId?: string,
    status?: string,
    options?: { skipCache?: boolean; limit?: number; offset?: number }
  ): Promise<{ tasks: RemediationTask[]; total: number; limit: number; offset: number }> {
    const params: Record<string, string | number> = {};
    if (status) params.status = status;
    // Default to a high limit to fetch all tasks, or use provided limit for pagination
    params.limit = options?.limit ?? 1000;
    if (options?.offset) params.offset = options.offset;

    if (domainId) {
      const baseKey = `GET:/api/domains/${domainId}/remediation-tasks?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()}`;
      return this.cachedRequest(
        this.cacheKey(baseKey),
        () => this.request("GET", `/api/domains/${domainId}/remediation-tasks`, { params }),
        options
      );
    }
    // Get all remediation tasks across all domains
    const baseKey = `GET:/api/remediation-tasks?${new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString()}`;
    return this.cachedRequest(
      this.cacheKey(baseKey),
      () => this.request("GET", `/api/remediation-tasks`, { params }),
      options
    );
  }

  async updateRemediationTask(
    taskId: string,
    data: { status?: RemediationTask["status"]; notes?: string }
  ): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("PATCH", `/api/remediation-tasks/${taskId}`, { body: data });
    this.clearCacheByPattern("/api/remediation-tasks");
    return result;
  }

  async completeTask(taskId: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("POST", `/api/remediation-tasks/${taskId}/complete`);
    this.clearCacheByPattern("/api/remediation-tasks");
    return result;
  }

  async skipTask(taskId: string, reason?: string): Promise<{ success: boolean }> {
    const result = await this.request<{ success: boolean }>("POST", `/api/remediation-tasks/${taskId}/skip`, {
      body: { reason },
    });
    this.clearCacheByPattern("/api/remediation-tasks");
    return result;
  }

  // User profile
  async getUserProfile(): Promise<{ profile: { id: string; name: string; email: string; role: string; created_at: string } }> {
    return this.request("GET", "/api/user/profile");
  }

  async updateUserProfile(data: { name?: string }): Promise<{ success: boolean }> {
    return this.request("PATCH", "/api/user/profile", { body: data });
  }

  // User preferences
  async getUserPreferences(): Promise<{ preferences: { weekly_digest: boolean; alert_emails: boolean; alert_severities?: string[] } }> {
    return this.request("GET", "/api/user/preferences");
  }

  async updateUserPreferences(
    prefs: Partial<{ weekly_digest: boolean; alert_emails: boolean; alert_severities: string[] }>
  ): Promise<{ preferences: { weekly_digest: boolean; alert_emails: boolean; alert_severities?: string[] } }> {
    return this.request("PATCH", "/api/user/preferences", { body: prefs });
  }

  // Events (Activity feed)
  async getEvents(params?: {
    limit?: number;
    offset?: number;
    event_type?: string;
  }): Promise<{
    events: Array<{
      id: string;
      event_type: string;
      entity_type?: string;
      entity_id?: string;
      user_name?: string;
      description?: string;
      created_at: string;
    }>;
    pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  }> {
    return this.request("GET", "/api/events", { params });
  }

  // Invitations
  async createInvitation(
    email: string,
    role: "admin" | "member" | "readonly" = "member"
  ): Promise<{ invitation: { id: string; email: string; role: string; expires_at: string } }> {
    return this.request("POST", "/api/invitations", { body: { email, role } });
  }

  async getInvitations(): Promise<{
    invitations: Array<{
      id: string;
      email: string;
      role: string;
      status: "pending" | "accepted" | "expired" | "revoked";
      expires_at: string;
      created_at: string;
    }>;
  }> {
    return this.request("GET", "/api/invitations");
  }

  async revokeInvitation(id: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/invitations/${id}`);
  }

  // Onboarding
  async completeOnboarding(): Promise<{ success: boolean }> {
    return this.request("POST", "/api/organization/complete-onboarding");
  }

  // Apex Links
  async getApexLinks(): Promise<{ apex_links: ApexLink[]; limits: { used: number; max: number | null } }> {
    return this.request("GET", "/api/apex-links");
  }

  async getApexLinkUsage(): Promise<{
    used: number;
    max: number;
    remaining: number;
    canGenerate: boolean;
    tier: string;
  }> {
    return this.request("GET", "/api/apex-links/usage");
  }

  async createApexLink(
    domainId: string,
    options?: {
      title?: string;
      sourceIds?: string[];
      providerIds?: string[];
      signingDomains?: string[];
      customMessage?: string;
      expiresInDays?: number;
    }
  ): Promise<{ apex_link: ApexLink; share_url: string }> {
    return this.request("POST", "/api/apex-links", {
      body: {
        domain_id: domainId,
        title: options?.title,
        source_ids: options?.sourceIds,
        provider_ids: options?.providerIds,
        signing_domains: options?.signingDomains,
        custom_message: options?.customMessage,
        expires_in_days: options?.expiresInDays,
      },
    });
  }

  async getApexLink(id: string): Promise<{ apex_link: ApexLink }> {
    return this.request("GET", `/api/apex-links/${id}`);
  }

  async revokeApexLink(id: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/apex-links/${id}`);
  }

  async getApexLinkAnalytics(id: string): Promise<{ analytics: ApexLinkAnalytics }> {
    return this.request("GET", `/api/apex-links/${id}/analytics`);
  }

  // Public Apex dashboard (no auth required)
  async getPublicApexDashboard(token: string): Promise<{
    domain: { name: string; pass_rate: number; policy: string };
    stats: { total_messages: number; pass_rate: number; spf_pass_rate: number; dkim_pass_rate: number };
    breakdown: Array<{ result: string; count: number }>;
    sources: Array<{
      id?: string;
      provider: string;
      identifier?: string;
      ptr_hostname?: string | null;
      source_type?: string | null;
      forwarding_detected?: boolean;
      label_category?: string | null;
      count: number;
      status: string;
      spf_pass_rate?: number;
      dkim_pass_rate?: number;
      issue?: string;
      fix?: string;
      is_expected?: boolean;
    }>;
    trend: Array<{ date: string; pass_rate: number; total_messages?: number }>;
    tasks: Array<{
      id: string;
      task_type: string;
      title: string;
      description: string;
      priority: string;
      status: string;
      suggested_record: string | null;
    }>;
    insights: Array<{
      id: string;
      type: string;
      severity: string;
      title: string;
      description: string;
      recommendations: string[];
      confidence: string;
      created_at: string;
    }>;
    branding: {
      logo_url: string | null;
      company_name: string | null;
      primary_color: string | null;
      accent_color: string | null;
    } | null;
    organization_name: string;
    is_source_specific: boolean;
    custom_message: string | null;
    generated_at?: string;
    provider_groups?: Array<{
      provider_id: string | null;
      provider_name: string;
      ip_count: number;
      total_volume: number;
      weighted_spf_rate: number;
      weighted_dkim_rate: number;
      weighted_spf_auth_rate: number;
      weighted_dkim_auth_rate: number;
      effective_status: string;
      source_ids: string[];
    }>;
    provider_info?: {
      id: string;
      name: string;
      category: string;
      sub_category: string | null;
      logo_url: string | null;
      documentation_url: string | null;
      setup_instructions: Record<string, unknown>;
      dkim_selectors: string[];
      spf_include: string | null;
      dkim_complexity: string | null;
      remediation_template: Record<string, unknown>;
      description: string | null;
    } | null;
    dkim_analysis?: {
      selectors: Array<{
        selector: string;
        signing_domain: string | null;
        pass: number;
        fail: number;
        total: number;
        pass_rate: number;
        aligned: boolean;
      }>;
      header_from_domains: string[];
      alignment_issue: boolean;
    } | null;
    disposition?: {
      none: number;
      quarantine: number;
      reject: number;
    };
    dmarc_record?: {
      policy: string;
      pct: number;
      sp: string | null;
      adkim: string | null;
      aspf: string | null;
    };
    enforcement?: {
      readiness_score: number;
      can_advance: boolean;
      next_policy: string | null;
      readiness_statement: string;
      blockers_count: number;
      spf_configured: boolean;
      dkim_configured: boolean;
    };
  }> {
    return this.request("GET", `/api/share/${token}`);
  }

  // Billing
  async getBilling(): Promise<{
    billing: {
      current_tier: string;
      subscription: {
        id: string;
        status: string;
        current_period_end: string | null;
        cancel_at_period_end: boolean;
      } | null;
      payment_method: {
        brand: string;
        last4: string;
      } | null;
      overage: {
        base_domains: number;
        current_domains: number;
        overage_domains: number;
        overage_amount_cents: number;
        next_invoice_total_cents: number;
      } | null;
      invoices: Array<{
        id: string;
        amount_cents: number;
        status: string;
        date: string;
        pdf_url?: string;
      }>;
    };
  }> {
    return this.request("GET", "/api/billing");
  }

  async createSetupIntent(): Promise<{ client_secret: string }> {
    return this.request("POST", "/api/billing/setup-intent");
  }

  async subscribe(options: {
    tier: string;
    billing_cycle?: "monthly" | "annual";
    trial_days?: number;
    signup_flow?: boolean;
  }): Promise<{
    checkout_url: string;
    session_id: string;
    error?: string;
    code?: string;
  }> {
    return this.request("POST", "/api/billing/subscribe", {
      body: {
        tier: options.tier,
        billing_cycle: options.billing_cycle || "monthly",
        trial_days: options.trial_days,
        signup_flow: options.signup_flow,
      },
    });
  }

  async createPortalSession(): Promise<{
    portal_url?: string;
    error?: string;
    code?: string;
  }> {
    return this.request("POST", "/api/billing/portal");
  }

  /**
   * Reconcile the org's billing state with Stripe. Used when the
   * in-app tier doesn't match what the user is actually paying for —
   * usually because a webhook delivery failed. Returns before/after
   * state and any warnings about drift or fallback resolution.
   */
  async resyncBilling(): Promise<{
    before: {
      billing_tier: string;
      billing_status: string;
      billing_cycle: string | null;
      stripe_customer_id: string | null;
      stripe_subscription_id: string | null;
      current_period_end: string | null;
      cancel_at_period_end: boolean;
    };
    after: {
      billing_tier: string;
      billing_status: string;
      billing_cycle: string | null;
      stripe_customer_id: string | null;
      stripe_subscription_id: string | null;
      current_period_end: string | null;
      cancel_at_period_end: boolean;
    };
    changed: boolean;
    source_subscription: {
      id: string;
      status: "active" | "trialing" | "past_due" | "unpaid";
      current_period_end: string | null;
      cancel_at_period_end: boolean;
      tier: string;
    } | null;
    warnings: string[];
  }> {
    return this.request("POST", "/api/billing/resync");
  }

  // Session Management
  async logout(): Promise<{ success: boolean }> {
    return this.request("POST", "/auth/logout");
  }

  async refreshToken(): Promise<{ token: string }> {
    return this.request("POST", "/auth/refresh", { skipTokenRefresh: true });
  }

  async logoutAll(): Promise<{ success: boolean }> {
    return this.request("POST", "/auth/logout-all");
  }

  async getSessions(): Promise<{
    sessions: Array<{
      id: string;
      device_info: string | null;
      ip_address: string | null;
      created_at: string;
      last_activity_at: string;
      expires_at: string;
    }>;
  }> {
    return this.request("GET", "/auth/sessions");
  }

  async revokeSession(sessionId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/auth/sessions/${sessionId}`);
  }

  // Account Deletion
  async requestAccountDeletion(reason?: string): Promise<{
    request_id: string;
    scheduled_deletion_at: string;
    message: string;
  }> {
    return this.request("POST", "/api/account/delete-request", {
      body: reason ? { reason } : {},
    });
  }

  async cancelAccountDeletion(): Promise<{ success: boolean; message: string }> {
    return this.request("POST", "/api/account/cancel-deletion");
  }

  async getAccountDeletionStatus(): Promise<{
    deletion_request: {
      id: string;
      status: "pending" | "processing" | "completed" | "canceled";
      reason: string | null;
      requested_at: string;
      scheduled_deletion_at: string;
      canceled_at: string | null;
    } | null;
  }> {
    return this.request("GET", "/api/account/deletion-status");
  }

  // DNS Integrations
  async getDnsIntegrations(): Promise<{ integrations: DnsIntegration[] }> {
    return this.request("GET", "/api/dns-integrations");
  }

  async getDnsIntegration(id: string): Promise<{ integration: DnsIntegration }> {
    return this.request("GET", `/api/dns-integrations/${id}`);
  }

  async connectGoDaddy(data: {
    apiKey: string;
    apiSecret: string;
    name?: string;
    domainId?: string;
  }): Promise<{ integration: DnsIntegration }> {
    return this.request("POST", "/api/dns-integrations/godaddy", { body: data });
  }

  async testDnsIntegration(id: string): Promise<{
    success: boolean;
    error?: string;
    zones?: DnsZone[];
  }> {
    return this.request("POST", `/api/dns-integrations/${id}/test`);
  }

  async deleteDnsIntegration(id: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/dns-integrations/${id}`);
  }

  // Domain DNS Integration
  async getDomainDnsIntegration(domainId: string): Promise<{
    integration: DnsIntegration | null;
    domain: { id: string; name: string };
  }> {
    return this.request("GET", `/api/domains/${domainId}/dns-integration`);
  }

  async detectDnsProvider(domainId: string): Promise<DnsDetectionResult> {
    return this.request("GET", `/api/domains/${domainId}/detect-provider`);
  }

  async getDomainDnsRecords(
    domainId: string,
    type?: string
  ): Promise<{ records: DnsRecord[] }> {
    return this.request("GET", `/api/domains/${domainId}/dns-records`, {
      params: type ? { type } : {},
    });
  }

  async createDnsRecord(
    domainId: string,
    record: { type: "TXT" | "CNAME" | "MX"; name: string; content: string; ttl?: number }
  ): Promise<{ record: DnsRecord }> {
    return this.request("POST", `/api/domains/${domainId}/dns-records`, {
      body: record,
    });
  }

  async getDomainDnsOperations(
    domainId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{
    operations: DnsOperation[];
    pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  }> {
    return this.request("GET", `/api/domains/${domainId}/dns-operations`, { params });
  }

  // Apply DNS from remediation task (for GoDaddy/API-based integrations)
  async applyRemediationDns(taskId: string): Promise<{
    success: boolean;
    record?: DnsRecord;
    operationType?: "create" | "update";
  }> {
    return this.request("POST", `/api/remediation-tasks/${taskId}/apply-dns`);
  }

  // ============================================
  // Domain Connect (for Cloudflare)
  // ============================================

  // Get Domain Connect URL for DMARC
  async getDomainConnectDmarcUrl(params: {
    domain: string;
    policy: "none" | "quarantine" | "reject";
    rua?: string;
    pct?: string;
    redirect_uri?: string;
    state?: string;
  }): Promise<{
    url: string;
    template: DomainConnectTemplate;
    variables: Record<string, string>;
  }> {
    return this.request("GET", "/api/dns-integrations/domain-connect/dmarc", { params });
  }

  // Get Domain Connect URL for SPF
  async getDomainConnectSpfUrl(params: {
    domain: string;
    spf_record: string;
    redirect_uri?: string;
    state?: string;
  }): Promise<{
    url: string;
    template: DomainConnectTemplate;
    variables: Record<string, string>;
  }> {
    return this.request("GET", "/api/dns-integrations/domain-connect/spf", { params });
  }

  // Get Domain Connect URL for domain verification
  async getDomainConnectVerifyUrl(params: {
    domain: string;
    token: string;
    redirect_uri?: string;
    state?: string;
  }): Promise<{
    url: string;
    template: DomainConnectTemplate;
    variables: Record<string, string>;
  }> {
    return this.request("GET", "/api/dns-integrations/domain-connect/verify", { params });
  }

  // List available Domain Connect templates
  async getDomainConnectTemplates(): Promise<{
    providerId: string;
    templates: Array<{
      serviceId: string;
      name: string;
      description: string;
      variables: Array<{ name: string; description: string; required: boolean; default?: string }>;
      records: Array<{ type: string; host: string; data?: string; ttl?: number }>;
    }>;
  }> {
    return this.request("GET", "/api/dns-integrations/domain-connect/templates");
  }

  // Apply DNS via Domain Connect for remediation task (returns URL for user to approve in Cloudflare)
  async applyRemediationDomainConnect(
    taskId: string,
    options?: { redirect_uri?: string; state?: string }
  ): Promise<{
    url: string;
    template: DomainConnectTemplate;
    variables: Record<string, string>;
    task: { id: string; type: string; domain: string };
  }> {
    return this.request("POST", `/api/remediation-tasks/${taskId}/apply-domain-connect`, {
      body: options || {},
    });
  }

  // ============================================
  // API Keys
  // ============================================

  // List all API keys for the organization
  async getApiKeys(): Promise<{
    keys: Array<{
      id: string;
      name: string;
      keyPreview: string;
      scopes: string[];
      lastUsedAt: string | null;
      expiresAt: string | null;
      revokedAt: string | null;
      createdAt: string;
      createdBy: string | null;
    }>;
  }> {
    return this.request("GET", "/api/keys");
  }

  // Create a new API key
  async createApiKey(params: {
    name: string;
    scopes?: string[];
    expiresInDays?: number;
  }): Promise<{
    key: {
      id: string;
      name: string;
      apiKey: string;
      keyPreview: string;
      scopes: string[];
      expiresAt: string | null;
      createdAt: string;
    };
    warning: string;
  }> {
    return this.request("POST", "/api/keys", { body: params });
  }

  // Revoke an API key
  async revokeApiKey(keyId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/keys/${keyId}`);
  }

  // ============================================
  // Analytics & Feature Tracking
  // ============================================

  /**
   * Track when a user attempts to access a gated feature.
   * Returns whether the feature was allowed based on their tier.
   */
  async trackFeatureUsage(params: {
    feature: string;
    context?: Record<string, unknown>;
  }): Promise<{
    success: boolean;
    wasAllowed: boolean;
    requiredTier?: string;
  }> {
    return this.request("POST", "/api/analytics/feature-usage", { body: params });
  }

  /**
   * Track when an upgrade prompt is shown to a user.
   * Returns a promptId for tracking clicks and conversions.
   */
  async trackUpgradePromptShown(params: {
    feature: string;
    location: string;
    targetTier: string;
  }): Promise<{
    success: boolean;
    promptId: string;
  }> {
    return this.request("POST", "/api/analytics/upgrade-prompt/shown", { body: params });
  }

  /**
   * Track when a user clicks the upgrade button on a prompt.
   */
  async trackUpgradePromptClick(promptId: string): Promise<{ success: boolean }> {
    return this.request("POST", "/api/analytics/upgrade-prompt/click", {
      body: { promptId },
    });
  }

  /**
   * Track when a user completes an upgrade (conversion).
   */
  async trackConversion(params: {
    promptId?: string;
    previousTier: string;
    newTier: string;
  }): Promise<{
    success: boolean;
    promptsUpdated: number;
  }> {
    return this.request("POST", "/api/analytics/conversion", { body: params });
  }

  /**
   * Get feature usage statistics for the organization.
   */
  async getFeatureUsageStats(period?: "7d" | "30d" | "90d"): Promise<{
    period: string;
    stats: Array<{
      featureName: string;
      totalAttempts: number;
      allowedAttempts: number;
      blockedAttempts: number;
      uniqueUsers: number;
    }>;
  }> {
    return this.request("GET", "/api/analytics/feature-usage/stats", {
      params: period ? { period } : {},
    });
  }

  /**
   * Get upgrade prompt effectiveness stats.
   */
  async getUpgradePromptStats(period?: "7d" | "30d" | "90d"): Promise<{
    period: string;
    stats: Array<{
      location: string;
      feature: string;
      impressions: number;
      clicks: number;
      conversions: number;
      clickThroughRate: number;
      conversionRate: number;
    }>;
  }> {
    return this.request("GET", "/api/analytics/upgrade-prompts/stats", {
      params: period ? { period } : {},
    });
  }

  /**
   * Get recently blocked features for insights.
   */
  async getBlockedFeatures(): Promise<{
    features: Array<{
      feature: string;
      count: number;
      lastAttempt: string;
    }>;
  }> {
    return this.request("GET", "/api/analytics/blocked-features");
  }

  // ============================================
  // UX Analytics (Page Views, Feature Discovery, etc.)
  // ============================================

  /**
   * Track a page view
   */
  async trackPageView(params: {
    sessionId: string;
    pagePath: string;
    pageName: string;
    referrerPath?: string | null;
    viewportWidth?: number;
    isMobile?: boolean;
  }): Promise<{ success: boolean; id: string }> {
    return this.request("POST", "/api/analytics/ux/page-view", { body: params });
  }

  /**
   * Update time spent on a page (called when navigating away)
   */
  async updatePageViewTime(
    pageViewId: string,
    timeOnPageMs: number
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/analytics/ux/page-view/${pageViewId}`, {
      body: { timeOnPageMs },
    });
  }

  /**
   * Track a help interaction
   */
  async trackHelpInteraction(params: {
    sessionId: string;
    interactionType: string;
    sourcePage?: string;
    searchQuery?: string;
    helpTopic?: string;
  }): Promise<{ success: boolean; id: string }> {
    return this.request("POST", "/api/analytics/ux/help", { body: params });
  }

  /**
   * Track feature discovery (first-time access)
   */
  async trackFeatureDiscovery(params: {
    featureKey: string;
    discoveryPath?: string;
    sourcePage?: string;
  }): Promise<{ success: boolean; isFirstDiscovery: boolean; id: string | null }> {
    return this.request("POST", "/api/analytics/ux/feature-discovery", {
      body: params,
    });
  }

  /**
   * Track a journey milestone
   */
  async trackMilestone(params: {
    milestone: string;
    domainId?: string;
    metadata?: Record<string, unknown>;
  }): Promise<{ success: boolean; isFirstTime: boolean; eventId: string | null }> {
    return this.request("POST", "/api/analytics/ux/milestone", { body: params });
  }

  /**
   * Get onboarding progress
   */
  async getOnboardingProgress(): Promise<{
    progress: {
      organizationId: string;
      steps: {
        addDomain: boolean;
        verifyDns: boolean;
        reviewSources: boolean;
        completeTask: boolean;
        inviteTeam: boolean;
      };
      checklistDismissed: boolean;
      dismissedAt: string | null;
      completedAt: string | null;
      completedCount: number;
      totalCount: number;
      showChecklist: boolean;
    };
    nextStep: {
      id: string;
      label: string;
      description: string;
      completed: boolean;
      requiredFor: "all" | "paid";
    } | null;
  }> {
    return this.request("GET", "/api/analytics/ux/onboarding");
  }

  /**
   * Dismiss the onboarding checklist
   */
  async dismissOnboardingChecklist(): Promise<{ success: boolean }> {
    return this.request("POST", "/api/analytics/ux/onboarding/dismiss");
  }

  /**
   * Get UX metrics summary (admin only)
   */
  async getUXMetrics(period?: "7d" | "30d" | "90d"): Promise<{
    period: string;
    summary: {
      totalSessions: number;
      avgPagesPerSession: number | null;
      helpPageVisits: number;
      helpSearches: number;
      tasksCreated: number;
      tasksCompleted: number;
      taskCompletionRate: number | null;
      avgTimeToCompleteHours: number | null;
      featuresDiscoveredIn7d: number;
    };
    journeyMilestones: Array<{
      milestone: string;
      days_since_signup: number;
    }>;
    topPages: Array<{
      page_path: string;
      page_name: string;
      views: number;
      unique_users: number;
      avg_time: number | null;
    }>;
  }> {
    return this.request("GET", "/api/analytics/ux/metrics", {
      params: period ? { period } : {},
    });
  }

  // ============================================
  // Audit & Compliance (Enterprise+)
  // ============================================

  /**
   * Get available audit report types for the organization.
   */
  async getAuditReportTypes(): Promise<{
    available: boolean;
    tier: string;
    report_types: Array<{
      id: string;
      name: string;
      description: string;
      formats: string[];
    }>;
    upgrade_message: string | null;
  }> {
    return this.request("GET", "/api/audit/report-types");
  }

  /**
   * Get audit event type reference.
   */
  async getAuditEventTypes(): Promise<{
    event_types: Array<{
      type: string;
      category: string;
      description: string;
    }>;
  }> {
    return this.request("GET", "/api/audit/event-types");
  }

  /**
   * Export audit logs as JSON.
   */
  async exportAuditJson(options?: {
    start_date?: string;
    end_date?: string;
    event_types?: string[];
    limit?: number;
    offset?: number;
  }): Promise<{
    events: Array<{
      id: string;
      event_type: string;
      entity_type: string | null;
      entity_id: string | null;
      description: string | null;
      metadata: Record<string, unknown>;
      ip_address: string | null;
      created_at: string;
      user_name: string | null;
      user_email: string | null;
    }>;
    export_metadata: {
      organization_id: string;
      exported_at: string;
      exported_by: string;
      filters: {
        start_date: string | null;
        end_date: string | null;
        event_types: string[] | null;
      };
      pagination: {
        total: number;
        limit: number;
        offset: number;
      };
    };
  }> {
    const params = new URLSearchParams();
    if (options?.start_date) params.set("start_date", options.start_date);
    if (options?.end_date) params.set("end_date", options.end_date);
    if (options?.event_types?.length) params.set("event_types", options.event_types.join(","));
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.offset) params.set("offset", options.offset.toString());

    const query = params.toString();
    return this.request("GET", `/api/audit/export/json${query ? `?${query}` : ""}`);
  }

  /**
   * Get CSV export URL for audit logs.
   */
  getAuditCsvUrl(options?: {
    start_date?: string;
    end_date?: string;
    event_types?: string[];
    max_rows?: number;
  }): string {
    const params = new URLSearchParams();
    if (options?.start_date) params.set("start_date", options.start_date);
    if (options?.end_date) params.set("end_date", options.end_date);
    if (options?.event_types?.length) params.set("event_types", options.event_types.join(","));
    if (options?.max_rows) params.set("max_rows", options.max_rows.toString());

    const query = params.toString();
    return `${API_BASE}/api/audit/export/csv${query ? `?${query}` : ""}`;
  }

  /**
   * Get PDF report URL.
   */
  getAuditPdfUrl(options?: {
    type?: "audit" | "compliance" | "executive";
    start_date?: string;
    end_date?: string;
  }): string {
    const params = new URLSearchParams();
    if (options?.type) params.set("type", options.type);
    if (options?.start_date) params.set("start_date", options.start_date);
    if (options?.end_date) params.set("end_date", options.end_date);

    const query = params.toString();
    return `${API_BASE}/api/audit/report/pdf${query ? `?${query}` : ""}`;
  }

  /**
   * Get HTML preview URL for report (for testing).
   */
  getAuditPreviewUrl(options?: {
    type?: "audit" | "compliance" | "executive";
    start_date?: string;
    end_date?: string;
  }): string {
    const params = new URLSearchParams();
    if (options?.type) params.set("type", options.type);
    if (options?.start_date) params.set("start_date", options.start_date);
    if (options?.end_date) params.set("end_date", options.end_date);

    const query = params.toString();
    return `${API_BASE}/api/audit/report/preview${query ? `?${query}` : ""}`;
  }

  // ============================================
  // IP Access Whitelist (Enterprise)
  // ============================================

  /**
   * Get IP restriction status for the organization.
   */
  async getIpWhitelistStatus(): Promise<{
    enabled: boolean;
    activeEntries: number;
    canManage: boolean;
    tier: string;
    currentIp: string;
  }> {
    return this.request("GET", "/api/ip-whitelist/status");
  }

  /**
   * Get all IP whitelist entries.
   */
  async getIpWhitelistEntries(options?: { include_expired?: boolean }): Promise<{
    entries: IpWhitelistEntry[];
    enabled: boolean;
    currentIp: string;
    tier: string;
    permissions: { canManageIpWhitelist: boolean };
  }> {
    return this.request("GET", "/api/ip-whitelist", {
      params: options?.include_expired ? { include_expired: "true" } : {},
    });
  }

  /**
   * Toggle IP restriction on/off.
   */
  async toggleIpRestriction(enabled: boolean): Promise<{
    success: boolean;
    enabled: boolean;
    message: string;
  }> {
    return this.request("POST", "/api/ip-whitelist/toggle", {
      body: { enabled },
    });
  }

  /**
   * Add an IP address to the whitelist.
   */
  async addIpToWhitelist(params: {
    ip_address: string;
    description?: string;
    expires_at?: string;
  }): Promise<{
    success: boolean;
    id: string;
    message: string;
  }> {
    return this.request("POST", "/api/ip-whitelist", { body: params });
  }

  /**
   * Add current IP to the whitelist.
   */
  async addCurrentIpToWhitelist(description?: string): Promise<{
    success: boolean;
    id: string;
    ip_address: string;
    message: string;
  }> {
    return this.request("POST", "/api/ip-whitelist/add-current", {
      body: { description },
    });
  }

  /**
   * Update an IP whitelist entry.
   */
  async updateIpWhitelistEntry(
    id: string,
    params: { description?: string; expires_at?: string | null; is_active?: boolean }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/ip-whitelist/${id}`, { body: params });
  }

  /**
   * Delete an IP from the whitelist.
   */
  async deleteIpFromWhitelist(id: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/ip-whitelist/${id}`);
  }

  // ============================================
  // SPF Optimization (AI-Powered)
  // ============================================

  /**
   * Resolve an SPF record to get full DNS lookup data
   */
  async resolveSpf(domainId: string): Promise<SpfResolveResult> {
    return this.request("GET", `/api/spf/${domainId}/resolution`);
  }

  /**
   * Run AI-powered SPF optimization
   */
  async optimizeSpf(
    domainId: string,
    params: {
      current_spf: string | null;
      resolved_data?: SpfResolveResult;
      optimization_goals?: string[];
    }
  ): Promise<SpfOptimizationResult> {
    return this.request("POST", `/api/spf/${domainId}/optimize`, {
      body: params,
    });
  }

  /**
   * Apply optimized SPF record
   */
  async applySpf(
    domainId: string,
    params: {
      optimization_id: string;
      method: "cloudflare" | "godaddy" | "domain_connect";
      redirect_uri?: string;
    }
  ): Promise<{ success: boolean; record?: DnsRecord }> {
    return this.request("POST", `/api/spf/${domainId}/apply`, {
      body: params,
    });
  }

  /**
   * Get SPF optimization history for a domain
   */
  async getSpfOptimizationHistory(
    domainId: string
  ): Promise<{
    optimizations: Array<{
      id: string;
      current_spf: string | null;
      suggested_spf: string;
      applied_at: string | null;
      lookup_count_before: number;
      lookup_count_after: number;
      risk_level: string;
      status: string;
    }>;
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> {
    return this.request("GET", `/api/spf/${domainId}/optimizations`);
  }

  // ============================================
  // MSP (Managed Service Provider) Routes
  // ============================================

  // Get MSP dashboard summary
  async getMspDashboard(): Promise<{
    summary: {
      total_clients: number;
      total_domains: number;
      total_users: number;
    };
    stats: {
      total_messages: number;
      avg_pass_rate: number;
      total_pass: number;
      total_fail: number;
    };
    clientsWithIssues: Array<{
      id: string;
      name: string;
      open_alerts: number;
      avg_pass_rate: number;
    }>;
    revenue: {
      estimated_monthly_cents: number;
    };
    partner: {
      tier: "reseller" | "white_label";
      wholesale_rate_cents: number;
    };
    profitability: {
      your_revenue_cents: number;
      verkh_cost_cents: number;
      margin_cents: number;
      margin_percent: number;
    };
  }> {
    return this.request("GET", "/api/msp/dashboard");
  }

  // List MSP client organizations
  async getMspClients(): Promise<{ clients: MspClientOrganization[] }> {
    return this.request("GET", "/api/msp/clients");
  }

  // Create a new MSP client organization
  async createMspClient(data: {
    name: string;
    billing_rate_cents_per_domain?: number;
    minimum_monthly_cents?: number;
  }): Promise<{ client: MspClientOrganization }> {
    return this.request("POST", "/api/msp/clients", { body: data });
  }

  // Get specific MSP client details
  async getMspClient(clientId: string): Promise<{
    client: MspClientOrganization;
    domains: Domain[];
  }> {
    return this.request("GET", `/api/msp/clients/${clientId}`);
  }

  // Update MSP client
  async updateMspClient(
    clientId: string,
    data: {
      name?: string;
      rateCentsPerDomain?: number;
      minimumMonthlyCents?: number;
      notes?: string;
    }
  ): Promise<{ client: MspClientOrganization }> {
    return this.request("PATCH", `/api/msp/clients/${clientId}`, { body: data });
  }

  // Delete MSP client
  async deleteMspClient(clientId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/clients/${clientId}`);
  }

  // Get domains for an MSP client
  async getMspClientDomains(clientId: string): Promise<{ domains: Domain[] }> {
    return this.request("GET", `/api/msp/clients/${clientId}/domains`);
  }

  // Add domain to MSP client
  async addMspClientDomain(
    clientId: string,
    domainName: string
  ): Promise<{ domain: Domain }> {
    return this.request("POST", `/api/msp/clients/${clientId}/domains`, {
      body: { domain_name: domainName },
    });
  }

  // Get MSP billing summary
  async getMspBillingSummary(): Promise<{
    summary: {
      total_monthly_revenue_cents: number;
      total_domains: number;
      client_count: number;
    };
    clients: Array<{
      client_id: string;
      client_name: string;
      rate_cents_per_domain: number;
      minimum_monthly_cents: number;
      domain_count: number;
      monthly_total_cents: number;
    }>;
  }> {
    return this.request("GET", "/api/msp/billing/summary");
  }

  // Get invoice billing summary (current month)
  async getMspInvoiceSummary(): Promise<{
    currentMonth: {
      clients: Array<{
        client_id: string;
        client_name: string;
        rate_cents_per_domain: number;
        minimum_monthly_cents: number;
        domain_count: number;
        current_month_cents: number;
      }>;
      total_cents: number;
    };
  }> {
    return this.request("GET", "/api/msp/invoices");
  }

  // List MSP invoices with pagination and filters
  async getMspInvoices(filters?: {
    status?: string;
    clientId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ invoices: MspInvoice[]; total: number; limit: number; offset: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.clientId) params.set("clientId", filters.clientId);
    if (filters?.limit) params.set("limit", String(filters.limit));
    if (filters?.offset) params.set("offset", String(filters.offset));
    const query = params.toString();
    return this.request("GET", `/api/msp/invoices/list${query ? `?${query}` : ""}`);
  }

  // Get single invoice
  async getMspInvoice(invoiceId: string): Promise<{ invoice: MspInvoice }> {
    return this.request("GET", `/api/msp/invoices/${invoiceId}`);
  }

  // Generate invoice for client
  async generateMspInvoice(data: {
    clientId: string;
    periodStart: string;
    periodEnd: string;
    dueDate?: string;
    notes?: string;
    lineItems?: MspInvoiceLineItem[];
    taxCents?: number;
  }): Promise<{ invoice: MspInvoice }> {
    return this.request("POST", "/api/msp/invoices/generate", { body: data });
  }

  // Update invoice
  async updateMspInvoice(
    invoiceId: string,
    data: {
      status?: "draft" | "sent" | "paid" | "cancelled";
      lineItems?: MspInvoiceLineItem[];
      notes?: string;
      taxCents?: number;
      dueDate?: string;
    }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/msp/invoices/${invoiceId}`, { body: data });
  }

  // Delete draft invoice
  async deleteMspInvoice(invoiceId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/invoices/${invoiceId}`);
  }

  // Get invoice PDF download URL
  getMspInvoicePdfUrl(invoiceId: string): string {
    return `${API_BASE}/api/msp/invoices/${invoiceId}/pdf`;
  }

  // Get invoice export URL (CSV for various accounting platforms)
  getMspInvoiceExportUrl(options: {
    format: "csv" | "quickbooks" | "xero";
    periodStart?: string;
    periodEnd?: string;
    status?: string;
  }): string {
    const params = new URLSearchParams();
    params.set("format", options.format);
    if (options.periodStart) params.set("periodStart", options.periodStart);
    if (options.periodEnd) params.set("periodEnd", options.periodEnd);
    if (options.status) params.set("status", options.status);
    return `${API_BASE}/api/msp/invoices/export?${params.toString()}`;
  }

  // Send invoice via email
  async sendMspInvoice(
    invoiceId: string,
    data?: {
      recipientEmail?: string;
      recipientName?: string;
    }
  ): Promise<{
    success: boolean;
    messageId?: string;
    sentTo: string;
    sentFrom: string;
    usedCustomDomain: boolean;
  }> {
    return this.request("POST", `/api/msp/invoices/${invoiceId}/send`, { body: data || {} });
  }

  // ============================================
  // MSP Email Domain APIs
  // ============================================

  // List verified email domains
  async getMspEmailDomains(): Promise<{ domains: MspEmailDomain[] }> {
    return this.request("GET", "/api/msp/email-domains");
  }

  // Add email domain for verification
  async addMspEmailDomain(data: {
    domain: string;
    defaultFromEmail?: string;
    defaultFromName?: string;
  }): Promise<{ domain: MspEmailDomain }> {
    return this.request("POST", "/api/msp/email-domains", { body: data });
  }

  // Verify email domain (check DNS records)
  async verifyMspEmailDomain(domainId: string): Promise<{
    status: "verified" | "pending" | "failed";
    dns_records: MspDnsRecord[];
  }> {
    return this.request("POST", `/api/msp/email-domains/${domainId}/verify`);
  }

  // Update email domain settings
  async updateMspEmailDomain(
    domainId: string,
    data: {
      defaultFromEmail?: string;
      defaultFromName?: string;
    }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/msp/email-domains/${domainId}`, { body: data });
  }

  // Delete email domain
  async deleteMspEmailDomain(domainId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/email-domains/${domainId}`);
  }

  // ============================================
  // MSP App Domains APIs (Custom white-label domains)
  // ============================================

  // Get all app domains for MSP
  async getMspAppDomains(): Promise<{ domains: MspAppDomain[] }> {
    return this.request("GET", "/api/msp/app-domains");
  }

  // Add app domain for verification
  async addMspAppDomain(data: { domain: string }): Promise<{ domain: MspAppDomain }> {
    return this.request("POST", "/api/msp/app-domains", { body: data });
  }

  // Verify app domain (check DNS records)
  async verifyMspAppDomain(domainId: string): Promise<{
    status: "verified" | "pending" | "failed";
    ssl_status?: string;
    dns_records: MspDnsRecord[];
  }> {
    return this.request("POST", `/api/msp/app-domains/${domainId}/verify`);
  }

  // Delete app domain
  async deleteMspAppDomain(domainId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/app-domains/${domainId}`);
  }

  // Set app domain as primary
  async setMspAppDomainPrimary(domainId: string): Promise<{ success: boolean }> {
    return this.request("POST", `/api/msp/app-domains/${domainId}/set-primary`);
  }

  // Refresh SSL status from Cloudflare
  async refreshMspAppDomainSsl(domainId: string): Promise<{
    ssl_status: "pending" | "active" | "expired";
    cf_ssl_status?: string;
    ssl_validation_records: SslValidationRecord[];
  }> {
    return this.request("POST", `/api/msp/app-domains/${domainId}/refresh-ssl`);
  }

  // ============================================
  // Public Branding API (for white-label domains)
  // ============================================

  // Get branding for current hostname (used on custom domains)
  async getBranding(): Promise<{
    type: "default" | "white_label";
    organizationId?: string;
    organizationName: string;
    logoUrl: string | null;
    primaryColor: string | null;
    customDomain?: string | null;
  }> {
    return this.request("GET", "/api/branding");
  }

  // ============================================
  // MSP Branding APIs
  // ============================================

  // Get MSP branding configuration
  async getMspBranding(): Promise<{ branding: MspBrandingConfig }> {
    return this.request("GET", "/api/msp/branding");
  }

  // Update MSP branding configuration
  async updateMspBranding(
    data: Partial<MspBrandingConfig>
  ): Promise<{ branding: MspBrandingConfig; message: string }> {
    return this.request("PATCH", "/api/msp/branding", { body: data });
  }

  // Get branding for a specific MSP client
  async getMspClientBranding(clientId: string): Promise<{
    branding: MspBrandingConfig;
    inherited_from_msp: boolean;
  }> {
    return this.request("GET", `/api/msp/clients/${clientId}/branding`);
  }

  // Update branding for a specific MSP client
  async updateMspClientBranding(
    clientId: string,
    data: Partial<MspBrandingConfig>
  ): Promise<{ branding: MspBrandingConfig; message: string }> {
    return this.request("PATCH", `/api/msp/clients/${clientId}/branding`, { body: data });
  }

  // ============================================
  // Team Invitations
  // ============================================

  // Invite a team member to the organization
  async inviteTeamMember(
    email: string,
    role: "admin" | "member",
    options?: { isMspAdmin?: boolean }
  ): Promise<{ success: boolean; invitation: { id: string; email: string } }> {
    return this.request("POST", "/api/invitations", {
      body: { email, role, is_msp_admin: options?.isMspAdmin },
    });
  }

  // ============================================
  // MSP Team Management APIs
  // ============================================

  // Get team members with assignment counts
  async getMspTeam(): Promise<{ team: MspTeamMember[]; total: number }> {
    return this.request("GET", "/api/msp/team");
  }

  // Get specific team member with their assignments
  async getMspTeamMember(userId: string): Promise<{
    member: MspTeamMember;
    assignments: MspClientAssignment[];
  }> {
    return this.request("GET", `/api/msp/team/${userId}`);
  }

  // Update team member (e.g., is_msp_admin flag)
  async updateMspTeamMember(
    userId: string,
    data: { is_msp_admin?: boolean }
  ): Promise<{ success: boolean }> {
    return this.request("PATCH", `/api/msp/team/${userId}`, { body: data });
  }

  // Remove team member from MSP
  async removeMspTeamMember(userId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/team/${userId}`);
  }

  // Get assignments for a team member
  async getMspTeamMemberAssignments(userId: string): Promise<{
    assignments: MspClientAssignment[];
  }> {
    return this.request("GET", `/api/msp/team/${userId}/assignments`);
  }

  // Bulk assign team member to clients
  async assignMspTeamMemberToClients(
    userId: string,
    data: { assignments: Array<{ client_id: string; access_level: string }> }
  ): Promise<{ success: boolean; created: number; updated: number }> {
    return this.request("POST", `/api/msp/team/${userId}/assignments`, { body: data });
  }

  // Remove team member assignment from a client
  async removeMspTeamMemberAssignment(
    userId: string,
    clientId: string
  ): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/msp/team/${userId}/assignments/${clientId}`);
  }

  // Get team members assigned to a specific client
  async getMspClientTeam(clientId: string): Promise<{
    team: Array<MspTeamMember & { access_level: string }>;
  }> {
    return this.request("GET", `/api/msp/clients/${clientId}/team`);
  }

  // ============================================
  // SSO Configuration APIs
  // ============================================

  // Get SSO configuration for current organization
  async getSsoConfig(): Promise<{
    config: SsoConfiguration | null;
    spSettings: { entityId: string; acsUrl: string; metadataUrl: string };
    emailDomains: SsoEmailDomain[];
  }> {
    return this.request("GET", "/api/sso/config");
  }

  // Configure SAML SSO
  async configureSamlSso(data: {
    providerName?: string;
    idpEntityId: string;
    idpSsoUrl: string;
    idpSloUrl?: string;
    idpCertificate: string;
    attributeMapping?: Record<string, string>;
    roleMapping?: Record<string, string[]>;
    defaultRole?: string;
    mspGroupMapping?: Record<string, { client_id: string; access_level: string }>;
    jitProvisioning?: boolean;
    allowIdpInitiated?: boolean;
  }): Promise<{
    success: boolean;
    spSettings: { entityId: string; acsUrl: string; metadataUrl: string };
  }> {
    return this.request("PUT", "/api/sso/saml", { body: data });
  }

  // Configure OIDC SSO
  async configureOidcSso(data: {
    providerName?: string;
    oidcIssuer: string;
    oidcClientId: string;
    oidcClientSecret: string;
    oidcScopes?: string;
    attributeMapping?: Record<string, string>;
    roleMapping?: Record<string, string[]>;
    defaultRole?: string;
    mspGroupMapping?: Record<string, { client_id: string; access_level: string }>;
    jitProvisioning?: boolean;
  }): Promise<{ success: boolean; redirectUrl: string }> {
    return this.request("PUT", "/api/sso/oidc", { body: data });
  }

  // Enable/disable SSO
  async setSsoEnabled(enabled: boolean): Promise<{ success: boolean; enabled: boolean }> {
    return this.request("PUT", "/api/sso/enable", { body: { enabled } });
  }

  // Enforce SSO (disable other login methods)
  async setSsoEnforced(enforce: boolean): Promise<{ success: boolean; enforce: boolean }> {
    return this.request("PUT", "/api/sso/enforce", { body: { enforce } });
  }

  // Get SSO login history
  async getSsoLoginHistory(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{ attempts: SsoLoginAttempt[]; total: number }> {
    const query = new URLSearchParams();
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    return this.request("GET", `/api/sso/login-history?${query}`);
  }

  // Add email domain for SSO
  async addSsoEmailDomain(emailDomain: string): Promise<{
    id: string;
    emailDomain: string;
    verified: boolean;
    verificationToken: string;
    verificationMethod: string;
  }> {
    return this.request("POST", "/api/sso/email-domain", { body: { emailDomain } });
  }

  // Verify email domain
  async verifySsoEmailDomain(domainId: string): Promise<{
    success: boolean;
    verified: boolean;
    message?: string;
  }> {
    return this.request("POST", `/api/sso/email-domain/${domainId}/verify`);
  }

  // Remove email domain
  async removeSsoEmailDomain(domainId: string): Promise<{ success: boolean }> {
    return this.request("DELETE", `/api/sso/email-domain/${domainId}`);
  }

  // ============================================
  // Development/Testing APIs (non-production only)
  // ============================================

  // Enable MSP mode for current organization (DEV ONLY)
  async devEnableMsp(partnerTier: "reseller" | "white_label" = "white_label"): Promise<{ success: boolean; message: string }> {
    return this.request("POST", "/api/dev/msp/enable", { body: { partner_tier: partnerTier } });
  }

  // Set partner tier for current MSP organization (DEV ONLY)
  async devSetPartnerTier(partnerTier: "reseller" | "white_label"): Promise<{ success: boolean; message: string }> {
    return this.request("POST", "/api/dev/msp/set-tier", { body: { partner_tier: partnerTier } });
  }

  // Seed test MSP clients with sample data (DEV ONLY)
  async devSeedMspClients(): Promise<{
    success: boolean;
    message: string;
    clients: Array<{
      id: string;
      name: string;
      domains: Array<{ id: string; domain_name: string }>;
      rate_cents_per_domain: number;
      minimum_monthly_cents: number;
    }>;
  }> {
    return this.request("POST", "/api/dev/msp/seed-clients");
  }

  // Disable MSP mode and remove test data (DEV ONLY)
  async devDisableMsp(): Promise<{ success: boolean; message: string }> {
    return this.request("POST", "/api/dev/msp/disable");
  }

  // ============================================
  // AI Recommendations (Proactive AI Insights)
  // ============================================

  // Get proactive AI recommendations from cached insights
  async getAIRecommendations(): Promise<AIRecommendationsResponse> {
    return this.cachedRequest(
      this.cacheKey("GET:/api/ai/recommendations"),
      () => this.request("GET", "/api/ai/recommendations"),
      { ttl: 60000 } // 1 minute cache
    );
  }

  // Approve an AI classification
  async approveAIClassification(sourceId: string): Promise<{
    success: boolean;
    sourceId: string;
    classification: string;
    message: string;
  }> {
    const result = await this.request<{
      success: boolean;
      sourceId: string;
      classification: string;
      message: string;
    }>("POST", `/api/ai/recommendations/${sourceId}/approve`);
    // Clear AI recommendations cache after action
    this.clearCacheByPattern("/api/ai/recommendations");
    return result;
  }

  // Reject an AI classification and optionally set a new one
  async rejectAIClassification(
    sourceId: string,
    options?: {
      newCategory?: string;
      newSubtype?: string;
      newStatus?: string;
      reason?: string;
    }
  ): Promise<{
    success: boolean;
    sourceId: string;
    newCategory?: string;
    newSubtype?: string;
    newStatus?: string;
    message: string;
  }> {
    const result = await this.request<{
      success: boolean;
      sourceId: string;
      newCategory?: string;
      newSubtype?: string;
      newStatus?: string;
      message: string;
    }>("POST", `/api/ai/recommendations/${sourceId}/reject`, {
      body: options || {},
    });
    // Clear AI recommendations cache after action
    this.clearCacheByPattern("/api/ai/recommendations");
    return result;
  }

  // ============================================
  // Action Items Summary
  // ============================================

  async getActionItemsSummary(domainId?: string, limit?: number): Promise<ActionItemsSummary> {
    const params: Record<string, string | number> = {};
    if (domainId) params.domainId = domainId;
    if (limit !== undefined) params.limit = limit;
    const queryString = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)])
    ).toString();
    const baseKey = `GET:/api/action-items/summary${queryString ? `?${queryString}` : ""}`;
    return this.cachedRequest(
      this.cacheKey(baseKey),
      () => this.request("GET", "/api/action-items/summary", Object.keys(params).length > 0 ? { params } : undefined),
      { ttl: 60000 }
    );
  }
}

// Action Items Summary types
export interface ActionItemsSummary {
  counts: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    bySource: { insights: number; tasks: number; alerts: number };
  };
  items: Array<{
    id: string;
    source: "alert" | "task" | "insight";
    title: string;
    description: string;
    priority: "critical" | "high" | "medium" | "low";
    isCompleted: boolean;
    createdAt: string;
    actionLink: string | null;
    domainId: string | null;
    domainName: string | null;
  }>;
  hasMore: boolean;
}

// AI Recommendations types (Proactive AI Insights)
export interface AIRecommendation {
  type: string;
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
  count: number;
  action: string;
  link: string;
  domains?: string[];
}

export interface AIPendingClassification {
  id: string;
  identifier: string;
  sourceName: string;
  providerName: string | null;
  domainName: string;
  domainId: string;
  totalMessages: number;
  currentStatus: string;
  suggestedCategory: string | null;
  suggestedSubtype: string | null;
  aiClassification: string;
  confidence: "high" | "medium" | "low";
  reasoning: string;
  analyzedAt: string;
  requiresApproval: boolean;
}

export interface AIEnforcementInsight {
  id: string;
  domainId: string;
  domainName: string;
  canAdvance: boolean;
  confidence: string;
  reasoning: string;
  riskLevel: string;
  riskDescription: string;
  recommendations: string[];
  analyzedAt: string;
}

export interface AIRecentAction {
  id: string;
  domainName: string;
  sourceName: string | null;
  sourceIdentifier: string | null;
  triggerType: string;
  actionTaken: string;
  classification: string;
  confidence: string;
  createdAt: string;
}

export interface AIConfigIssue {
  type: "config_issue";
  priority: "high" | "medium";
  sourceId: string;
  sourceIdentifier: string;
  sourceName: string;
  domainName: string;
  domainId: string;
  totalMessages: number;
  issues: string[];
  recommendation: string;
}

export interface AICachedInsight {
  id: string;
  domainId: string | null;
  sourceId: string | null;
  type: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  title: string;
  description: string;
  metadata: Record<string, unknown>;
  recommendations: string[];
  actionLink: string | null;
  confidence: string;
  createdAt: string;
  status?: "active" | "snoozed" | "resolved" | "not_applicable";
  snoozedUntil?: string | null;
}

export interface AIInsightSuppression {
  id: string;
  domainId: string | null;
  domainName?: string | null;
  insightType: string;
  fingerprint: string;
  createdAt: string;
}

export interface AIRecommendationsResponse {
  available: boolean;
  tier: string;
  recommendations: AIRecommendation[];
  pendingClassifications: AIPendingClassification[];
  enforcementInsights: AIEnforcementInsight[];
  recentActions: AIRecentAction[];
  configIssues: AIConfigIssue[];
  insights?: AICachedInsight[];
  generatedAt: string;
}

// MSP Client Organization type
export interface MspClientOrganization extends Organization {
  domain_count?: number;
  dmarc_pass_rate?: number;
  enforcement_percentage?: number;
  last_report_at?: string | null;
  rate_cents_per_domain?: number;
  minimum_monthly_cents?: number;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
}

// MSP Invoice types
export interface MspInvoiceLineItem {
  description: string;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
}

export type MspInvoiceStatus = "draft" | "sent" | "paid" | "overdue" | "cancelled";

export interface MspInvoice {
  id: string;
  msp_organization_id: string;
  client_organization_id: string;
  client_name?: string;
  client_email?: string;
  invoice_number?: string;
  period_start: string;
  period_end: string;
  line_items: MspInvoiceLineItem[] | string; // JSON string from DB or parsed array
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  status: MspInvoiceStatus;
  notes?: string | null;
  pdf_key?: string | null;
  email_sent_to?: string | null;
  email_sent_from?: string | null;
  email_message_id?: string | null;
  external_invoice_id?: string | null;
  external_invoice_url?: string | null;
  created_at: string;
  sent_at?: string | null;
  paid_at?: string | null;
  due_date?: string | null;
}

// MSP Email Domain types
export interface MspDnsRecord {
  type: string;
  name: string;
  value: string;
  priority?: number;
  ttl?: number;
  status?: "pending" | "verified" | "not_started";
}

export interface MspEmailDomain {
  id: string;
  msp_organization_id: string;
  domain: string;
  resend_domain_id?: string | null;
  status: "pending" | "verified" | "failed";
  dns_records: MspDnsRecord[] | string; // JSON string from DB or parsed array
  default_from_email?: string | null;
  default_from_name?: string | null;
  created_at: string;
  verified_at?: string | null;
  last_checked_at?: string | null;
}

// MSP App Domain (custom white-label domain for the web app)
export interface SslValidationRecord {
  txt_name: string;
  txt_value: string;
}

export interface MspAppDomain {
  id: string;
  msp_organization_id?: string;
  domain: string;
  status: "pending" | "verified" | "failed";
  dns_records: MspDnsRecord[] | string;
  ssl_status: "pending" | "active" | "expired" | null;
  cf_custom_hostname_id?: string | null;
  cf_ssl_status?: string | null;
  ssl_validation_records?: SslValidationRecord[] | string;
  is_primary: boolean | number;
  created_at: string;
  verified_at?: string | null;
  updated_at?: string;
}

// MSP Branding configuration
export interface MspBrandingConfig {
  logo_url?: string | null;
  favicon_url?: string | null;
  primary_color?: string;
  accent_color?: string;
  company_name?: string | null;
  support_email?: string | null;
  support_url?: string | null;
  custom_css?: string | null;
}

// Domain Connect template type for API responses
export interface DomainConnectTemplate {
  providerId: string;
  serviceId: string;
  name: string;
  description: string;
  variableDescription?: string;
  syncPubKeyDomain: string;
  syncBlock: boolean;
  shared: boolean;
  multiInstance: boolean;
  version: number;
  logoUrl?: string;
  variables?: Array<{ name: string; description: string; required: boolean; default?: string }>;
  records: Array<{ type: string; host: string; data?: string; ttl?: number }>;
}

// MSP Team Management types
export interface MspTeamMember {
  id: string;
  email: string;
  name: string | null;
  role: string;
  is_msp_admin: boolean | number; // SQLite returns 0/1
  assigned_client_count?: number;
  created_at: string;
  last_login_at?: string | null;
}

export interface MspClientAssignment {
  id: string;
  client_organization_id: string;
  client_name: string;
  access_level: "full" | "readonly" | "billing_only";
  assigned_by_user_id?: string;
  assigned_by_name?: string;
  created_at: string;
}

// SSO Configuration types
export type SsoProviderType = "saml" | "oidc";
export type SsoProviderName = "okta" | "azure_ad" | "google" | "onelogin" | "custom";

export interface SsoConfiguration {
  id: string;
  provider_type: SsoProviderType;
  provider_name?: SsoProviderName | string;
  // SAML settings
  idp_entity_id?: string;
  idp_sso_url?: string;
  idp_slo_url?: string;
  // OIDC settings
  oidc_issuer?: string;
  oidc_client_id?: string;
  oidc_scopes?: string;
  // SP settings
  sp_entity_id: string;
  sp_acs_url: string;
  // Mappings
  attribute_mapping: Record<string, string>;
  role_mapping: Record<string, string[]>;
  default_role: string;
  msp_group_mapping: Record<string, { client_id: string; access_level: string }>;
  // Behavior
  jit_provisioning: boolean;
  enforce_sso: boolean;
  allow_idp_initiated: boolean;
  enabled: boolean;
  verified_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SsoEmailDomain {
  id: string;
  email_domain: string;
  verified: boolean;
  verified_at?: string | null;
  verification_token?: string;
}

export interface SsoLoginAttempt {
  id: string;
  provider_type: SsoProviderType;
  status: "success" | "failed" | "pending";
  error_code?: string;
  error_message?: string;
  email?: string;
  name?: string;
  groups?: string[];
  created_user: boolean;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export const api = new ApiClient();
